import { useEffect, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import {
  Container,
  Row,
  Table as BTable,
  Spinner,
  Button,
  Modal,
  Form,
  Dropdown,
} from "react-bootstrap";
import { toast } from "react-toastify";
import { TableCell } from "@/components/Elements/TableCell";
import InfiniteScrollSelect from "@/components/Elements/InfiniteScrollSelect";
import { managerApi, ordersApi, activityApi } from "@/api";
import { matchApi } from "@/api/match-api";
import { TEAM_COLORS, getTeamColorValue } from "@/constants/teams";
import { axiosInstance } from "@/core/http";

const Orders = () => {
  const { user } = useSelector(({ auth }) => auth);
  const [orders, setOrders] = useState({});
  const [link, setLink] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const [pageNum, setPageNum] = useState(1);
  const [sorting, setSorting] = useState([
    {
      id: "created",
      desc: "true",
    },
  ]);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [orderType, setOrderType] = useState("");
  const [isValidUrl, setIsValidUrl] = useState(false);
  const [activityDate, setActivityDate] = useState("");
  const [assignVideo, setAssignVideo] = useState("now");
  const [videoStart, setVideoStart] = useState("");
  const [videoEnd, setVideoEnd] = useState("");
  const [cameraPosition, setCameraPosition] = useState("");
  const [selectedCamera, setSelectedCamera] = useState("");
  const [cameras, setCameras] = useState([]);
  const [isLoadingCameras, setIsLoadingCameras] = useState(false);
  const [camerasPage, setCamerasPage] = useState(1);
  const [camerasHasMore, setCamerasHasMore] = useState(false);
  const [camerasCount, setCamerasCount] = useState(0);
  const [showStatsModal, setShowStatsModal] = useState(false);
  const [selectedStats, setSelectedStats] = useState(null);
  const [orderSubtype, setOrderSubtype] = useState("");
  const [teamsAmount, setTeamsAmount] = useState(1);
  const [showTeamsModal, setShowTeamsModal] = useState(false);
  const [editingOrder, setEditingOrder] = useState(null);
  const [editTeams, setEditTeams] = useState({
    homeTeam: { name: "", color: "", striped: false },
    guestTeam: { name: "", color: "", striped: false },
    orderTeam: "",
    trainingTeam: { name: "", color: "", striped: false },
  });
  const [newFirstTeam, setNewFirstTeam] = useState("");
  const [newSecondTeam, setNewSecondTeam] = useState("");
  const [newTrainingTeam, setNewTrainingTeam] = useState("");
  const [coachTeams, setCoachTeams] = useState([]);
  const [teamsPage, setTeamsPage] = useState(1);
  const [teamsHasMore, setTeamsHasMore] = useState(false);
  const [teamsLoading, setTeamsLoading] = useState(false);
  const [teamsCount, setTeamsCount] = useState(0);
  const [isEditingStats, setIsEditingStats] = useState(false);
  const [editedStats, setEditedStats] = useState(null);
  const [isCalculatingStats, setIsCalculatingStats] = useState(false);
  const [calculatingOrderId, setCalculatingOrderId] = useState(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [orderToCancel, setOrderToCancel] = useState(null);
  const [isCancelling, setIsCancelling] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelReasonText, setCancelReasonText] = useState("");
  const [showCameraModal, setShowCameraModal] = useState(false);
  const [editingCameraOrder, setEditingCameraOrder] = useState(null);
  const [selectedCameraPosition, setSelectedCameraPosition] = useState("");
  const [isUpdatingCamera, setIsUpdatingCamera] = useState(false);
  const [showMatchesModal, setShowMatchesModal] = useState(false);
  const [matchesLoading, setMatchesLoading] = useState(false);
  const [orderMatches, setOrderMatches] = useState([]);
  const [matchesOrderId, setMatchesOrderId] = useState(null);
  const [editingMatch, setEditingMatch] = useState(null);
  const [showColorModal, setShowColorModal] = useState(false);
  const [bindUnbindLoading, setBindUnbindLoading] = useState({});
  const [showActivitySetupModal, setShowActivitySetupModal] = useState(false);
  const [activitySetupOrder, setActivitySetupOrder] = useState(null);
  const [activitySetupActivityId, setActivitySetupActivityId] = useState(null);
  const [activityPlayersPerTeam, setActivityPlayersPerTeam] = useState("");
  const [isSavingPlayersPerTeam, setIsSavingPlayersPerTeam] = useState(false);
  const [matchTimes, setMatchTimes] = useState({});
  const [savingMatchTimes, setSavingMatchTimes] = useState({});
  const [loadingActivitySetup, setLoadingActivitySetup] = useState(false);

  // Copy activity modal state
  const [showCopyActivityModal, setShowCopyActivityModal] = useState(false);
  const [copyActivityOrder, setCopyActivityOrder] = useState(null);
  const [copyActivityEnvironment, setCopyActivityEnvironment] = useState("devbox");
  const [externalClients, setExternalClients] = useState([]);
  const [externalClientsPage, setExternalClientsPage] = useState(1);
  const [externalClientsCount, setExternalClientsCount] = useState(0);
  const [externalClientsHasMore, setExternalClientsHasMore] = useState(false);
  const [externalClientsLoading, setExternalClientsLoading] = useState(false);
  const [selectedExternalClientName, setSelectedExternalClientName] =
    useState("");
  const [isCopyingActivity, setIsCopyingActivity] = useState(false);

  // Protocol modal state
  const [showProtocolModal, setShowProtocolModal] = useState(false);
  const [protocolData, setProtocolData] = useState(null);
  const [protocolLoading, setProtocolLoading] = useState(false);
  const [protocolActivityId, setProtocolActivityId] = useState(null);
  const [protocolUpdating, setProtocolUpdating] = useState(false);

  // Change type modal state
  const [showChangeTypeModal, setShowChangeTypeModal] = useState(false);
  const [changeTypeOrder, setChangeTypeOrder] = useState(null);
  const [changeTypeValue, setChangeTypeValue] = useState("");
  const [changeSubtypeValue, setChangeSubtypeValue] = useState("");
  const [isChangingType, setIsChangingType] = useState(false);

  // Add new state for order search
  const [orderSearchQuery, setOrderSearchQuery] = useState("");
  const [isOrderSearching, setIsOrderSearching] = useState(false);

  // Add new state for multi-step order creation
  const [createOrderStep, setCreateOrderStep] = useState(1);
  const [tempSelectedCamera, setTempSelectedCamera] = useState("");
  const [selectedArena, setSelectedArena] = useState("");
  const [tempOrderData, setTempOrderData] = useState(null);
  const [tempNewFirstTeam, setTempNewFirstTeam] = useState("");
  const [tempNewSecondTeam, setTempNewSecondTeam] = useState("");
  const [tempEditTeams, setTempEditTeams] = useState({
    homeTeam: { name: "", color: "", striped: false },
    guestTeam: { name: "", color: "", striped: false },
    orderTeam: "",
    trainingTeam: { name: "", color: "", striped: false },
  });

  const getActivityIdFromOrder = (order) =>
    order?.activity ||
    order?.activity_id ||
    order?.activityId ||
    order?.activity?.id ||
    order?.activity_pk ||
    null;

  const getArenaKey = (camera) => {
    if (camera?.arena_id != null) return String(camera.arena_id);
    if (camera?.arena != null) return String(camera.arena);
    if (camera?.arena_name) return `name:${camera.arena_name}`;
    return null;
  };

  const mapExternalClientOption = (client, index) => {
    const userId = client?.id ?? client?.user_id ?? client?.pk ?? null;
    const name =
      client?.name ??
      client?.full_name ??
      client?.username ??
      client?.email ??
      (userId != null ? `User ${userId}` : `User ${index + 1}`);

    return {
      id: `${userId ?? name}-${index}`,
      name,
      userId,
      raw: client,
    };
  };

  const fetchExternalClients = async ({ environment, page = 1, append = false }) => {
    if (!environment) return;

    try {
      setExternalClientsLoading(true);

      const response = await ordersApi.getExternalClientList(environment, page);
      // Support both shapes:
      // - axios default: response.data.results
      // - this app's axiosInstance interceptor: returns response.data directly (so response.results)
      const results =
        response?.data?.results ?? response?.results ?? response?.data ?? response ?? [];
      const count =
        response?.data?.count ??
        response?.count ??
        (Array.isArray(results) ? results.length : 0);

      const mapped = Array.isArray(results)
        ? results.map((c, idx) =>
            mapExternalClientOption(c, (page - 1) * 10000 + idx)
          )
        : [];

      setExternalClientsCount(count);
      setExternalClientsPage(page);

      setExternalClients((prev) => {
        const next = append ? [...prev, ...mapped] : mapped;
        setExternalClientsHasMore(next.length < count);
        return next;
      });
    } catch (err) {
      console.log(err);
      toast.error(
        err.response?.data?.message ||
          JSON.stringify(err.response?.data)?.slice(0, 200) ||
          err.message ||
          "Failed to fetch users for selected environment."
      );
    } finally {
      setExternalClientsLoading(false);
    }
  };

  const openCopyActivityModal = async (order) => {
    setCopyActivityOrder(order);
    setShowCopyActivityModal(true);
    setSelectedExternalClientName("");
    setExternalClients([]);
    setExternalClientsPage(1);
    setExternalClientsCount(0);
    setExternalClientsHasMore(false);

    await fetchExternalClients({
      environment: copyActivityEnvironment,
      page: 1,
      append: false,
    });
  };

  const closeCopyActivityModal = () => {
    setShowCopyActivityModal(false);
    setCopyActivityOrder(null);
    setSelectedExternalClientName("");
    setExternalClients([]);
    setExternalClientsPage(1);
    setExternalClientsCount(0);
    setExternalClientsHasMore(false);
    setExternalClientsLoading(false);
    setIsCopyingActivity(false);
  };

  const loadMoreExternalClients = async () => {
    if (externalClientsLoading || !externalClientsHasMore) return;
    await fetchExternalClients({
      environment: copyActivityEnvironment,
      page: externalClientsPage + 1,
      append: true,
    });
  };

  const openActivitySetupModal = async (order) => {
    setActivitySetupOrder(order);
    const activityId = getActivityIdFromOrder(order);
    setActivitySetupActivityId(activityId);
    setShowActivitySetupModal(true);
    setLoadingActivitySetup(true);

    try {
      // Load players per team for the activity (if available)
      if (activityId) {
        try {
          const activityData = await activityApi.getPlayersPerTeam(activityId);
          if (
            activityData &&
            typeof activityData.players_per_team !== "undefined"
          ) {
            setActivityPlayersPerTeam(activityData.players_per_team);
          } else {
            setActivityPlayersPerTeam("");
          }
        } catch (err) {
          console.log(err);
          toast.error(
            err.response?.data?.message ||
              JSON.stringify(err.response?.data)?.slice(0, 200) ||
              err.message ||
              "Failed to load players per team for activity."
          );
        }
      } else {
        setActivityPlayersPerTeam("");
      }

      // Load matches related to this order and their times
      try {
        const res = await ordersApi.getOrderMatches(order.id);
        const matches = res.results || [];
        setOrderMatches(matches);

        const times = {};
        await Promise.all(
          matches.map(async (match) => {
            try {
              const matchTimeData = await matchApi.getMatchTime(match.id);
              if (
                matchTimeData &&
                typeof matchTimeData.match_time !== "undefined"
              ) {
                times[match.id] = matchTimeData.match_time;
              }
            } catch (err) {
              console.log(err);
            }
          })
        );
        setMatchTimes(times);
      } catch (err) {
        console.log(err);
        toast.error(
          err.response?.data?.message ||
            JSON.stringify(err.response?.data)?.slice(0, 200) ||
            err.message ||
            "Failed to load matches for activity setup."
        );
        setOrderMatches([]);
        setMatchTimes({});
      }
    } finally {
      setLoadingActivitySetup(false);
    }
  };

  const columns = [
    {
      accessorKey: "created",
      header: "Created",
      enableSorting: true,
      cell: (info) => {
        const date = new Date(info.getValue());

        // Formatting options
        const options = {
          year: "numeric",
          month: "short",
          day: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
        };

        // Convert to human-readable format using user's local timezone
        const formattedDate = date.toLocaleDateString("en-US", options);
        return <>{formattedDate}</>;
      },
    },
    {
      header: "Order",
      cell: (info) => <>{info.row.original.id}</>,
    },
    {
      header: "Activity",
      cell: (info) => <>{info.row.original.activity}</>,
    },
    {
      header: "Game Date",
      cell: (info) => {
        const activityDate = info.row.original.activity_date;
        if (!activityDate) return <>-</>;

        const date = new Date(activityDate);

        // Formatting options - always show date only
        const options = {
          year: "numeric",
          month: "short",
          day: "numeric",
        };

        // Convert to human-readable format
        const formattedDate = date.toLocaleDateString("en-US", options);
        return <>{formattedDate}</>;
      },
    },
    {
      accessorKey: "owner_name",
      header: "Owner Name",
    },
    {
      accessorKey: "order_type",
      header: "Order Info",
      cell: (info) => {
        const orderType = info.row.original.order_type;
        const orderSubtype = info.row.original.order_subtype;
        const field = info.row.original.field;
        const cameraPosition = info.row.original.camera_position;
        return (
          <div className="tw-flex tw-flex-col">
            <span>{orderType}</span>
            {orderSubtype && <span>{orderSubtype}</span>}
            {field && <span>{field}</span>}
            {cameraPosition && <span>Camera: {cameraPosition}</span>}
          </div>
        );
      },
    },
    {
      header: "Teams",
      cell: (info) => {
        const homeTeam = info.row.original.home_team;
        const guestTeam = info.row.original.guest_team;
        const orderTeam = info.row.original.order_team_name;
        const teamsAmount = info.row.original.teams_amount;
        const orderType = info.row.original.order_type;

        if (orderType === "training_analytics") {
          // For training analytics, show only training team
          return (
            <div className="tw-flex tw-flex-col">
              <span>
                Training: {homeTeam?.name || "No team"}
                {homeTeam?.color && homeTeam?.name && ", "}
                {homeTeam?.color &&
                  `${homeTeam.color
                    .charAt(0)
                    .toUpperCase()}${homeTeam.color.slice(1)}`}
                {homeTeam?.striped && "-stripe"}
              </span>
            </div>
          );
        }

        const isMAPro = orderType === "match_analytics_pro";
        const label1 = isMAPro ? "first" : "home";
        const label2 = isMAPro ? "second" : "guest";

        return (
          <div className="tw-flex tw-flex-col">
            <span>
              {homeTeam?.name && `${label1}: ${homeTeam.name}`}
              {homeTeam?.color && homeTeam?.name && ", "}
              {homeTeam?.color &&
                `${homeTeam.color
                  .charAt(0)
                  .toUpperCase()}${homeTeam.color.slice(1)}`}
              {homeTeam?.striped && "-stripe"}
            </span>
            <span>
              {guestTeam?.name && `${label2}: ${guestTeam.name}`}
              {guestTeam?.color && guestTeam?.name && ", "}
              {guestTeam?.color &&
                `${guestTeam.color
                  .charAt(0)
                  .toUpperCase()}${guestTeam.color.slice(1)}`}
              {guestTeam?.striped && "-stripe"}
            </span>
            {!isMAPro && (
              <span>
                {teamsAmount === 2
                  ? "order: BOTH"
                  : orderTeam && `order: ${orderTeam}`}
              </span>
            )}
          </div>
        );
      },
    },
    {
      accessorKey: "highlighted_players",
      header: "Ordered Players",
      cell: (info) => {
        const highlighted_players = info && info.row.original.highlight_players;
        return (
          <>
            {highlighted_players &&
              highlighted_players.length &&
              highlighted_players.map((highlighted_player) => (
                <div className="tw-flex tw-items-center">
                  {highlighted_player.number && (
                    <span className="tw-min-w-24">
                      Number: {highlighted_player.number},
                    </span>
                  )}
                  {
                    <span>
                      Team: {highlighted_player.team === 0 ? "Home" : "Guest"}
                    </span>
                  }
                </div>
              ))}
          </>
        );
      },
    },
    {
      accessorKey: "finalised",
      header: "Status",
      cell: (info) => {
        const finalised = info.row.original.finalised;
        const orderStatus = info.row.original.status;
        const cancelled = info.row.original.cancelled;

        // Determine status text and styling
        let statusText = "";
        let statusClass = "";
        let cancelReasonText = "";

        if (cancelled !== null) {
          // Order is cancelled
          statusText = "CANCELLED";
          statusClass = "tw-text-orange-600 tw-font-semibold";
          cancelReasonText = info.row.original.cancel_reason;
        } else if (finalised) {
          // Order is finalized
          const date = new Date(finalised);
          const options = {
            year: "numeric",
            month: "short",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
          };
          const formattedDate = date.toLocaleDateString("en-US", options);
          statusText = "FINALIZED";
          statusClass = "tw-text-green-600 tw-font-semibold";
          return (
            <div className="tw-flex tw-justify-center tw-flex-col">
              <span className={statusClass}>{statusText}</span>
              <span className="tw-text-black tw-text-sm">{formattedDate}</span>
            </div>
          );
        } else if (orderStatus === "pending") {
          // Order is in progress
          statusText = "IN PROGRESS";
          statusClass = "tw-text-blue-600 tw-font-semibold";
        } else {
          // Default case for other statuses
          statusText = "IN PROGRESS";
          statusClass = "tw-text-blue-600 tw-font-semibold";
        }

        return (
          <div className="tw-flex tw-justify-center tw-flex-col">
            <span className={statusClass}>{statusText}</span>
            {cancelReasonText && (
              <span className="tw-text-black tw-text-sm">
                {cancelReasonText}
              </span>
            )}
          </div>
        );
      },
    },
    {
      accessorKey: "stats",
      header: "ACTIONS",
      cell: (info) => {
        const order = info.row.original;
        const orderType = order.order_type;
        const orderStatus = order.status;
        const orderCheckLink = order.order_check_link;
        const hasStats = info.getValue() !== null;
        const isCurrentlyCalculating = calculatingOrderId === order.id;
        const isFinalised = order.finalised !== null;

        const isMatchAnalytics =
          orderType === "match_analytics" ||
          orderType === "match_analytics_pro";

        // Define all actions in the required sequence
        const allActions = [
          {
            key: "check",
            label: "Check",
            action: () => {
              window.open(orderCheckLink, "_blank", "noreferrer");
            },
            disabled:
              !orderCheckLink || isMatchAnalytics || order.cancelled !== null,
          },
          {
            key: "calcGoals",
            label: "Calc Goals",
            action: async () => {
              try {
                await ordersApi.updateScore(order.id);

                // Update orders list to reflect changes
                const updatedOrders = await ordersApi.getOrdersList();
                if (!updatedOrders) return;
                setOrders(updatedOrders?.results);
                setTotalCount(updatedOrders?.count);

                toast.success("Successfully calculated goals.");
              } catch (err) {
                console.log(err);
                toast.error(
                  err.response?.data?.message || "Failed to calculate goals."
                );
              }
            },
            disabled:
              (!["binding", "pending"].includes(orderStatus) && !isFinalised) ||
              ["match_analytics", "match_analytics_pro"].includes(orderType) ||
              order.cancelled !== null,
          },
          {
            key: "stats",
            label: hasStats
              ? "Stats"
              : isCurrentlyCalculating
              ? "Calculating..."
              : "Stats",
            action: async () => {
              if (hasStats) {
                // View existing stats
                setSelectedStats(info.getValue());
                setShowStatsModal(true);
                setEditingOrder(order);
              } else {
                // Calculate new stats
                try {
                  setIsCalculatingStats(true);
                  setCalculatingOrderId(order.id);

                  // Call API to calculate stats and get the result
                  const result = await ordersApi.updateOrderStats(
                    order.id,
                    null
                  );

                  // If we got stats back immediately, show them in the modal
                  if (result && result.stats) {
                    setSelectedStats(result.stats);
                    setEditingOrder(order);
                    setShowStatsModal(true);
                  }

                  // Update orders list to reflect changes
                  const updatedOrders = await ordersApi.getOrdersList();
                  if (!updatedOrders) return;
                  setOrders(updatedOrders?.results);
                  setTotalCount(updatedOrders?.count);

                  toast.success("Successfully started stats calculation.");
                } catch (err) {
                  // Even if calculation fails, open the modal with empty stats for manual entry
                  setSelectedStats(null);
                  setEditingOrder(order);
                  setShowStatsModal(true);
                  setIsEditingStats(true); // Automatically enter edit mode
                  setEditedStats("{}"); // Start with empty JSON object

                  toast.error(
                    err.response?.data?.message ||
                      "Failed to start stats calculation. You can enter stats manually."
                  );
                } finally {
                  setIsCalculatingStats(false);
                  setCalculatingOrderId(null);
                }
              }
            },
            disabled:
              !["match_analytics", "match_analytics_pro"].includes(orderType) ||
              isCurrentlyCalculating ||
              order.cancelled !== null,
          },
          {
            key: "sendStats",
            label: "Send Stats",
            action: async () => {
              try {
                await ordersApi.sendTeamAnalytics(order.id);
                toast.success("Successfully sent team analytics stats.");
              } catch (err) {
                console.log(err);
                toast.error(
                  err.response?.data?.message ||
                    JSON.stringify(err.response?.data)?.slice(0, 200) ||
                    err.message ||
                    "Something went wrong while sending team analytics stats."
                );
              }
            },
            disabled:
              !["team_analytics", "team_analytics_pro"].includes(orderType) ||
              order.cancelled !== null,
          },
          {
            key: "teamsEdit",
            label: "Teams Edit",
            action: () => {
              const homeTeam = order.home_team;
              const guestTeam = order.guest_team;
              const orderTeam = order.order_team_name;
              const teamsAmount = order.teams_amount;

              setEditingOrder(order);

              if (order.order_type === "training_analytics") {
                // For training analytics, initialize with training team data
                setEditTeams({
                  homeTeam: { name: "", color: "", striped: false },
                  guestTeam: { name: "", color: "", striped: false },
                  orderTeam: "",
                  trainingTeam: homeTeam || {
                    name: "",
                    color: "",
                    striped: false,
                  },
                });
                setNewTrainingTeam("");
              } else {
                // For other order types, use regular team initialization
                // Determine the orderTeam value based on team names
                let orderTeamValue = "";
                if (
                  teamsAmount === 2 ||
                  order.order_type === "match_analytics_pro"
                ) {
                  orderTeamValue = "both";
                } else if (orderTeam === homeTeam?.name) {
                  orderTeamValue = "home_team";
                } else if (orderTeam === guestTeam?.name) {
                  orderTeamValue = "guest_team";
                } else {
                  orderTeamValue = orderTeam || "";
                }

                setEditTeams({
                  homeTeam: homeTeam || { name: "", color: "", striped: false },
                  guestTeam: guestTeam || {
                    name: "",
                    color: "",
                    striped: false,
                  },
                  orderTeam: orderTeamValue,
                  trainingTeam: { name: "", color: "", striped: false },
                });
                setNewFirstTeam("");
                setNewSecondTeam("");
              }

              setShowTeamsModal(true);

              // Fetch teams with pagination for edit modal
              const fetchTeamsForEdit = async () => {
                try {
                  setTeamsLoading(true);
                  const response = await managerApi.getCoachTeams(order.owner, 1);
                  const teams = response?.results || [];
                  const count = response?.count || 0;
                  setCoachTeams(teams);
                  setTeamsCount(count);
                  setTeamsHasMore(teams.length < count);
                  setTeamsPage(1);
                } catch (err) {
                  console.error("Error fetching coach teams:", err);
                  toast.error("Failed to fetch coach teams");
                } finally {
                  setTeamsLoading(false);
                }
              };
              fetchTeamsForEdit();
            },
            disabled: order.cancelled !== null, // Disable if order is cancelled
          },
          {
            key: "changeType",
            label: "Change Type",
            action: () => {
              setChangeTypeOrder(order);
              setChangeTypeValue(order.order_type || "");
              setChangeSubtypeValue(order.order_subtype || "");
              setShowChangeTypeModal(true);
            },
            disabled: order.cancelled !== null,
          },
          {
            key: "camera",
            label: "Camera",
            action: () => {
              setEditingCameraOrder(order);
              setSelectedCameraPosition(order.camera_position || "");
              setShowCameraModal(true);
            },
            disabled: order.cancelled !== null, // Disable if order is cancelled
          },
          {
            key: "matches",
            label: "Matches",
            action: async () => {
              setMatchesOrderId(order.id);
              setShowMatchesModal(true);
              await refreshOrderMatches(order.id);
            },
            disabled: order.cancelled !== null, // Disable if order is cancelled
          },
          {
            key: "activitySetup",
            label: "Set activity setup",
            action: () => {
              openActivitySetupModal(order);
            },
            disabled: order.cancelled !== null, // Disable if order is cancelled
          },
          {
            key: "copyActivity",
            label: "Copy activity",
            action: () => {
              openCopyActivityModal(order);
            },
            disabled: order.cancelled !== null || !getActivityIdFromOrder(order),
          },
          {
            key: "protocol",
            label: "Protocol",
            action: async () => {
              const activityId = getActivityIdFromOrder(order);
              if (!activityId) return;
              try {
                setProtocolLoading(true);
                setShowProtocolModal(true);
                setProtocolActivityId(activityId);
                const data = await ordersApi.getMatchProtocol(activityId);
                setProtocolData(data);
              } catch (err) {
                console.log(err);
                toast.error(
                  err.response?.data?.message || "Failed to load protocol."
                );
                setShowProtocolModal(false);
                setProtocolActivityId(null);
              } finally {
                setProtocolLoading(false);
              }
            },
            disabled:
              order.cancelled !== null || !getActivityIdFromOrder(order) || !order.is_qjleague,
          },
          {
            key: "finalize",
            label: "Finalize",
            action: async () => {
              try {
                await ordersApi.finaliseOrder(order.id);
                const updatedOrders = await ordersApi.getOrdersList();
                if (!updatedOrders) return;
                setOrders(updatedOrders?.results);
                setTotalCount(updatedOrders?.count);
                toast.success("Successfully finalised the order.");
              } catch (err) {
                console.log(err);
                toast.error(
                  err.response?.data?.message ||
                    JSON.stringify(err.response?.data)?.slice(0, 200) ||
                    err.message ||
                    "Something went wrong while finalising the order."
                );
              }
            },
            disabled: order.finalised !== null || order.cancelled !== null, // Disable if order is already finalized or cancelled
          },
          {
            key: "cancel",
            label: "Cancel",
            action: () => {
              setOrderToCancel(order);
              setShowCancelModal(true);
            },
            disabled: order.cancelled !== null || order.finalised !== null, // Disable if already cancelled or finalized
          },
          {
            key: "resetFormations",
            label: "Reset Formations",
            action: async () => {
              if (!window.confirm("Reset all formation changes to starting only?")) return;
              try {
                const res = await ordersApi.resetFormations(order.id);
                toast.success(`Formations reset (${res.data?.matches_updated || 0} matches updated)`);
              } catch (err) {
                toast.error(err.response?.data?.error || "Failed to reset formations");
              }
            },
            disabled: order.cancelled !== null || !getActivityIdFromOrder(order),
          },
        ];

        return (
          <Dropdown>
            <Dropdown.Toggle
              // align="end"
              variant="outline-secondary"
              size="sm"
              title="Actions"
              id={`action-dropdown-${order.id}`}
            >
              Actions
            </Dropdown.Toggle>
            <Dropdown.Menu className="tw-transform !tw-translate-x-[-80px] !tw-translate-y-[33px]">
              {allActions.map((action, idx) => (
                <Dropdown.Item
                  key={action.key}
                  eventKey={idx + 1}
                  onClick={action.action}
                  disabled={action.disabled}
                >
                  {action.label}
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>
        );
      },
    },
  ];

  const table = useReactTable({
    data: orders,
    columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    getCoreRowModel: getCoreRowModel(),
  });

  const formatStats = (stats) => {
    if (!stats) return "";
    // Create a deep copy of stats to avoid modifying the original
    const filteredStats = JSON.parse(JSON.stringify(stats));

    // Only filter out events when in view mode, not edit mode
    if (!isEditingStats) {
      if (filteredStats?.first_team) {
        delete filteredStats.first_team.Events;
        delete filteredStats.first_team.events;
      }
      if (filteredStats?.second_team) {
        delete filteredStats.second_team.Events;
        delete filteredStats.second_team.events;
      }
    }

    return JSON.stringify(filteredStats, null, 2);
  };

  useEffect(() => {
    if (!user) return;

    async function prepareData() {
      try {
        setIsLoading(true);
        const orders = await ordersApi.getOrdersList(1, "");
        if (!orders) return;
        setOrders(orders?.results);
        setTotalCount(orders?.count);
        setPageNum(1);
      } catch (err) {
        toast.error(
          err.response?.data?.message ||
            JSON.stringify(err.response?.data)?.slice(0, 200) ||
            err.message ||
            "Something went wrong while fetching current orders."
        );
      } finally {
        setIsLoading(false);
      }
    }
    prepareData();
  }, []); // Remove orderSearchQuery dependency since we're not doing automatic search

  // Fetch all playgrounds upfront so the arena list is complete
  useEffect(() => {
    if (createOrderStep === 2 && orderType === "broadcasting" && cameras.length === 0) {
      const fetchAllCameras = async () => {
        try {
          setIsLoadingCameras(true);
          const first = await axiosInstance.get("/panel/playground/", { params: { page: 1 } });
          const firstResults = first?.results ?? (Array.isArray(first) ? first : []);
          const count = first?.count ?? firstResults.length;
          const pageSize = firstResults.length || 1;
          const totalPages = Math.ceil(count / pageSize);

          let allResults = [...firstResults];
          if (totalPages > 1) {
            const remaining = await Promise.all(
              Array.from({ length: totalPages - 1 }, (_, i) =>
                axiosInstance.get("/panel/playground/", { params: { page: i + 2 } })
              )
            );
            for (const res of remaining) {
              const r = res?.results ?? (Array.isArray(res) ? res : []);
              allResults = allResults.concat(r);
            }
          }

          setCameras(allResults);
          setCamerasCount(allResults.length);
          setCamerasHasMore(false);
        } catch (err) {
          console.error("Error fetching cameras:", err);
          toast.error("Failed to fetch cameras");
        } finally {
          setIsLoadingCameras(false);
        }
      };
      fetchAllCameras();
    }
  }, [createOrderStep, orderType]);

  // Fetch coach teams when reaching step 4 (teams configuration)
  useEffect(() => {
    if (createOrderStep === 4 && coachTeams.length === 0 && selectedClient) {
      const fetchCoachTeams = async () => {
        try {
          setTeamsLoading(true);
          const response = await managerApi.getCoachTeams(selectedClient.id, 1);
          const teams = response?.results || [];
          const count = response?.count || 0;
          setCoachTeams(teams);
          setTeamsCount(count);
          setTeamsHasMore(teams.length < count);
          setTeamsPage(1);
        } catch (err) {
          console.error("Error fetching coach teams:", err);
          toast.error("Failed to fetch coach teams");
        } finally {
          setTeamsLoading(false);
        }
      };
      fetchCoachTeams();
    }
  }, [createOrderStep, selectedClient]);

  // Load more teams for infinite scroll
  const loadMoreTeams = useCallback(async () => {
    const currentUserId = selectedClient?.id || editingOrder?.owner;
    if (!currentUserId || teamsLoading || !teamsHasMore) return;

    try {
      setTeamsLoading(true);
      const nextPage = teamsPage + 1;
      const response = await managerApi.getCoachTeams(currentUserId, nextPage);
      const newTeams = response?.results || [];
      const count = response?.count || 0;
      
      setCoachTeams((prev) => {
        const updated = [...prev, ...newTeams];
        setTeamsHasMore(updated.length < count);
        return updated;
      });
      setTeamsPage(nextPage);
    } catch (err) {
      console.error("Error loading more teams:", err);
      toast.error("Failed to load more teams");
    } finally {
      setTeamsLoading(false);
    }
  }, [selectedClient?.id, editingOrder?.owner, teamsPage, teamsLoading, teamsHasMore]);

  // Monitor modal and step state changes
  useEffect(() => {
    console.log(
      "Modal state changed - showModal:",
      showModal,
      "createOrderStep:",
      createOrderStep
    );
  }, [showModal, createOrderStep]);

  const fetchMoreOrders = async () => {
    try {
      setPageNum(pageNum + 1);
      const res = await ordersApi.getOrdersList(pageNum + 1, "");
      setOrders([...orders, ...res.results]);
    } catch (err) {
      console.error("Error fetching data:", err);
      toast.error(
        err.response?.data?.message ||
          JSON.stringify(err.response?.data)?.slice(0, 200) ||
          err.message ||
          "Something went wrong while fetching data."
      );
    }
  };

  const handleLoadMore = async () => {
    setLoadingMore(true);
    await fetchMoreOrders();
    setLoadingMore(false);
  };

  const createOrder = () => {
    console.log("createOrder called, setting showModal to true");
    setShowModal(true);
  };

  const onCancel = () => {
    console.log("onCancel called");
    setShowModal(false);
    // Reset step and clear temporary data
    setCreateOrderStep(1);
    setTempOrderData(null);
    setTempNewFirstTeam("");
    setTempNewSecondTeam("");
    setTempSelectedCamera("");
    setSelectedArena("");
    setTempEditTeams({
      homeTeam: { name: "", color: "", striped: false },
      guestTeam: { name: "", color: "", striped: false },
      orderTeam: "",
      trainingTeam: { name: "", color: "", striped: false },
    });
    setCoachTeams([]);
    setTeamsPage(1);
    setTeamsHasMore(false);
    setTeamsCount(0);
    setCameras([]);
    setCamerasPage(1);
    setCamerasHasMore(false);
    setCamerasCount(0);
    setSelectedCamera("");
    setActivityDate("");
    setAssignVideo("now");
    setVideoStart("");
    setVideoEnd("");
  };

  const onNext = (e) => {
    // Prevent any default form submission behavior
    if (e) e.preventDefault();

    console.log("onNext called, current step:", createOrderStep);

    if (createOrderStep === 1) {
      // Step 1 -> Step 2: Just move to next step (client and order type already selected)
      setCreateOrderStep(2);
    } else if (createOrderStep === 2) {
      // Step 2 -> Step 3: Store camera/video URL and activity date data
      const orderData = {
        activity_type: orderType,
        link: link || "https://bigbro.ai",
      };

      if (assignVideo !== "later") {
        if (orderType === "broadcasting") {
          // For broadcasting: activity_date = video_start, and include video_start and video_end
          orderData.activity_date = videoStart;
          orderData.video_start = videoStart;
          orderData.video_end = videoEnd;
          orderData.playground = tempSelectedCamera;
        } else {
          // For non-broadcasting: just activity_date
          orderData.activity_date = activityDate;
        }
      }
      orderData.assign_video = assignVideo;

      // Add order_subtype and teams_amount if applicable
      if (["team_analytics", "team_analytics_pro"].includes(orderType)) {
        orderData.order_subtype = orderSubtype;
        orderData.teams_amount = teamsAmount;
      }

      setTempOrderData(orderData);
      if (orderType === "broadcasting") {
        setSelectedCamera(tempSelectedCamera);
      }
      setCreateOrderStep(3);
    } else if (createOrderStep === 3) {
      // Step 3 -> Step 4: Store camera position
      setTempOrderData({
        ...tempOrderData,
        camera_position: cameraPosition,
      });
      setCreateOrderStep(4);
    }
  };

  const onBack = () => {
    if (createOrderStep === 2) {
      setCreateOrderStep(1);
    } else if (createOrderStep === 3) {
      setCreateOrderStep(2);
    } else if (createOrderStep === 4) {
      setCreateOrderStep(3);
      setCoachTeams([]);
    }
  };

  const onCreate = async () => {
    setIsCreating(true);
    try {
      // Use the stored order data from all steps
      const orderData = {
        ...tempOrderData,
        camera_position: tempOrderData?.camera_position || cameraPosition,
      };

      // Ensure we have all required fields
      if (!orderData.activity_type) orderData.activity_type = orderType;
      if (!orderData.link) {
        orderData.link = link || "https://bigbro.ai";
      }
      
      // Handle date fields based on order type
      if (orderData.assign_video === "later") {
        // Don't send video/date/playground fields when assigning later
        delete orderData.activity_date;
        delete orderData.video_start;
        delete orderData.video_end;
        delete orderData.playground;
      } else if (orderType === "broadcasting") {
        // For broadcasting: activity_date = video_start, and include video_start and video_end
        if (!orderData.activity_date) {
          orderData.activity_date = videoStart;
        }
        if (!orderData.video_start) {
          orderData.video_start = videoStart;
        }
        if (!orderData.video_end) {
          orderData.video_end = videoEnd;
        }
        // Handle playground field
        if (!orderData.playground) {
          orderData.playground = tempOrderData?.playground || selectedCamera;
        }
      } else {
        // For non-broadcasting: just activity_date
        if (!orderData.activity_date) {
          orderData.activity_date = activityDate;
        }
        // Remove playground and video fields for non-broadcasting orders
        delete orderData.playground;
        delete orderData.video_start;
        delete orderData.video_end;
      }

      console.log("tempEditTeams", tempEditTeams);

      // Create order without teams data first
      const result = await ordersApi.createOrder(orderData, selectedClient?.id);
      if (!result) return;

      const orderId = result.order_id;

      // If we have teams information, update teams separately
      if (tempOrderData?.activity_type === "training_analytics") {
        // For training analytics, use training team data
        if (tempEditTeams.trainingTeam.name || tempNewFirstTeam) {
          const teamsData = {
            home_team: {
              id: tempNewFirstTeam
                ? null
                : coachTeams.find(
                    (team) => team.name === tempEditTeams.trainingTeam.name
                  )?.id,
              name: tempNewFirstTeam || tempEditTeams.trainingTeam.name,
              color: tempEditTeams.trainingTeam.color,
              striped: tempEditTeams.trainingTeam.striped,
            },
            guest_team: null, // No guest team for training
            order_team: null, // No order team for training
          };

          // Update teams for the newly created order
          await ordersApi.updateOrderTeams(orderId, teamsData);
        }
      } else {
        // For other order types, use regular team data
        if (tempEditTeams.homeTeam.name || tempNewFirstTeam) {
          const teamsData = {
            home_team: {
              id: tempNewFirstTeam
                ? null
                : coachTeams.find(
                    (team) => team.name === tempEditTeams.homeTeam.name
                  )?.id,
              name: tempNewFirstTeam || tempEditTeams.homeTeam.name,
              color: tempEditTeams.homeTeam.color,
              striped: tempEditTeams.homeTeam.striped,
            },
            guest_team: {
              id: tempNewSecondTeam
                ? null
                : coachTeams.find(
                    (team) => team.name === tempEditTeams.guestTeam.name
                  )?.id,
              name: tempNewSecondTeam || tempEditTeams.guestTeam.name,
              color: tempEditTeams.guestTeam.color,
              striped: tempEditTeams.guestTeam.striped,
            },
            order_team:
              tempOrderData?.activity_type === "match_analytics_pro"
                ? "both"
                : tempEditTeams.orderTeam,
          };

          // Update teams for the newly created order
          await ordersApi.updateOrderTeams(orderId, teamsData);
        }
      }

      // Fetch updated orders list with current search query
      const updatedOrders = await ordersApi.getOrdersList(1, orderSearchQuery);
      if (updatedOrders) {
        setOrders(updatedOrders?.results);
        setTotalCount(updatedOrders?.count);
      }

      setIsCreating(false);
      setShowModal(false);
      // Reset step and clear temporary data
      setCreateOrderStep(1);
      setTempOrderData(null);
      setTempNewFirstTeam("");
      setTempNewSecondTeam("");
      setTempEditTeams({
        homeTeam: { name: "", color: "", striped: false },
        guestTeam: { name: "", color: "", striped: false },
        orderTeam: "",
        trainingTeam: { name: "", color: "", striped: false },
      });
      setCoachTeams([]);
      setTeamsPage(1);
      setTeamsHasMore(false);
      setTeamsCount(0);
      toast.success("Successfully created the order.");
    } catch (err) {
      const errorMessage =
        err.response?.data?.message ||
        JSON.stringify(err.response?.data)?.slice(0, 200) ||
        "Something went wrong.";
      toast.error(errorMessage);
      setIsCreating(false);
    }
  };

  // Add debounced search function
  const debouncedSearch = useCallback(
    debounce(async (query) => {
      if (query.length < 1) {
        setSearchResults([]);
        return;
      }

      setIsSearching(true);
      try {
        const { results } = await ordersApi.searchClients(query);
        setSearchResults(results);
      } catch (error) {
        toast.error("Error searching for clients");
        console.error(error);
      } finally {
        setIsSearching(false);
      }
    }, 300),
    []
  );

  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    debouncedSearch(query);
  };

  // Add debounce utility function at the top of the file
  function debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  const handleClientSelect = (client) => {
    setSelectedClient(client);
    setSearchResults([]); // Clear search results after selection
  };

  const handleOrderTypeChange = (e) => {
    setOrderType(e.target.value);
  };

  const validateUrl = (url) => {
    try {
      new URL(url);
      return true;
    } catch (err) {
      return false;
    }
  };

  const handleUrlChange = (e) => {
    const url = e.target.value;
    setLink(url);
    setIsValidUrl(validateUrl(url));
  };

  const handleSavePlayersPerTeam = async () => {
    if (!activitySetupActivityId) {
      toast.error("This order is not linked to an activity.");
      return;
    }

    const value = Number(activityPlayersPerTeam);
    if (!value || value <= 0) {
      toast.error("Please enter a valid players per team value.");
      return;
    }

    try {
      setIsSavingPlayersPerTeam(true);
      await activityApi.updatePlayersPerTeam(activitySetupActivityId, value);
      toast.success("Players per team updated successfully.");
    } catch (err) {
      console.log(err);
      toast.error(
        err.response?.data?.message ||
          JSON.stringify(err.response?.data)?.slice(0, 200) ||
          err.message ||
          "Something went wrong while updating players per team."
      );
    } finally {
      setIsSavingPlayersPerTeam(false);
    }
  };

  const handleMatchTimeChange = (matchId, value) => {
    setMatchTimes((prev) => ({
      ...prev,
      [matchId]: value,
    }));
  };

  const handleSaveMatchTime = async (matchId) => {
    const value = Number(matchTimes[matchId]);

    if (!value || value <= 0) {
      toast.error("Please enter a valid match time.");
      return;
    }

    setSavingMatchTimes((prev) => ({ ...prev, [matchId]: true }));
    try {
      await matchApi.updateMatchTime(matchId, value);
      toast.success("Match time updated successfully.");
    } catch (err) {
      console.log(err);
      toast.error(
        err.response?.data?.message ||
          JSON.stringify(err.response?.data)?.slice(0, 200) ||
          err.message ||
          "Something went wrong while updating match time."
      );
    } finally {
      setSavingMatchTimes((prev) => ({ ...prev, [matchId]: false }));
    }
  };

  // Helper to refresh matches
  const refreshOrderMatches = async (orderId) => {
    setMatchesLoading(true);
    try {
      const res = await ordersApi.getOrderMatches(orderId);
      setOrderMatches(res.results || []);
    } catch (err) {
      toast.error("Failed to fetch matches");
      setOrderMatches([]);
    } finally {
      setMatchesLoading(false);
    }
  };

  // Bind match to order
  const handleBindMatch = async (matchId) => {
    setBindUnbindLoading((prev) => ({ ...prev, [matchId]: true }));
    try {
      await matchApi.bindMatchToOrder(matchId, matchesOrderId);
      toast.success("Successfully bound match to order.");
      await refreshOrderMatches(matchesOrderId);
    } catch (err) {
      const errorMessage =
        err.response?.data?.message ||
        JSON.stringify(err.response?.data)?.slice(0, 200) ||
        "Something went wrong while binding match to order.";
      toast.error(errorMessage);
    } finally {
      setBindUnbindLoading((prev) => ({ ...prev, [matchId]: false }));
    }
  };

  // Unbind match from order
  const handleUnbindMatch = async (matchId) => {
    setBindUnbindLoading((prev) => ({ ...prev, [matchId]: true }));
    try {
      await matchApi.unbindMatchFromOrder(matchId);
      toast.success("Successfully unbound match from order.");
      await refreshOrderMatches(matchesOrderId);
    } catch (err) {
      const errorMessage =
        err.response?.data?.message ||
        JSON.stringify(err.response?.data)?.slice(0, 200) ||
        "Something went wrong while unbinding match from order.";
      toast.error(errorMessage);
    } finally {
      setBindUnbindLoading((prev) => ({ ...prev, [matchId]: false }));
    }
  };

  // Edit team colors for a match
  const handleColorEdit = async () => {
    try {
      await matchApi.updateTeamColors(editingMatch.id, {
        leftTeamColor: editTeams.homeTeam.color,
        rightTeamColor: editTeams.guestTeam.color,
      });
      await refreshOrderMatches(matchesOrderId);
      setShowColorModal(false);
      toast.success("Colors updated successfully");
    } catch (err) {
      console.log(err);
      toast.error("Failed to update colors");
    }
  };

  // Handle order search input change (no automatic search)
  const handleOrderSearchChange = (e) => {
    setOrderSearchQuery(e.target.value);
  };

  // Manual search function for the Filter button and Enter key
  const handleOrderSearch = async () => {
    setIsOrderSearching(true);
    try {
      const orders = await ordersApi.getOrdersList(1, orderSearchQuery);
      if (!orders) return;
      setOrders(orders?.results);
      setTotalCount(orders?.count);
      setPageNum(1);
    } catch (err) {
      toast.error(
        err.response?.data?.message ||
          JSON.stringify(err.response?.data)?.slice(0, 200) ||
          err.message ||
          "Something went wrong while searching orders."
      );
    } finally {
      setIsOrderSearching(false);
    }
  };

  return (
    <Container className="py-3" fluid>
      <Row className="d-flex justify-content-center">
        <div className="tw-flex tw-justify-between tw-relative">
          <h2 className="tw-flex-1 tw-flex tw-justify-center tw-text-center tw-font-medium tw-tracking-wider">
            Orders
          </h2>

          {/* Search Bar */}
          <div className="tw-absolute tw-right-32 tw-flex tw-items-center tw-self-center tw-gap-2">
            <Form.Control
              type="text"
              placeholder="Search by Order ID or Activity ID..."
              value={orderSearchQuery}
              onChange={handleOrderSearchChange}
              onKeyPress={(e) => {
                if (e.key === "Enter") {
                  handleOrderSearch();
                }
              }}
              className="tw-w-64"
            />
            <Button
              variant="outline-secondary"
              onClick={handleOrderSearch}
              disabled={isOrderSearching}
              className="tw-px-4"
            >
              {isOrderSearching ? (
                <>
                  <Spinner animation="border" size="sm" className="tw-mr-2" />
                  Filtering...
                </>
              ) : (
                "Filter"
              )}
            </Button>
          </div>

          <Button
            className="tw-absolute tw-right-6 tw-w-fit tw-px-4 tw-self-center disabled:!tw-cursor-not-allowed disabled:tw-bg-gray-500 tw-border-none"
            onClick={createOrder}
          >
            Create
          </Button>
        </div>
        {orders.length ? (
          <div
            style={{
              maxWidth: "100%",
              maxHeight: "80vh",
              overflowX: "auto",
              overflowY: "auto",
            }}
          >
            <BTable responsive hover size="md">
              <thead>
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <th
                        key={header.id}
                        colSpan={header.colSpan}
                        onClick={header.column.getToggleSortingHandler()}
                        style={{
                          cursor: header.column.getCanSort()
                            ? "pointer"
                            : "default",
                        }}
                      >
                        {header.isPlaceholder ? null : (
                          <div className="d-flex align-items-center">
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                            {{
                              asc: " 🔼",
                              desc: " 🔽",
                            }[header.column.getIsSorted()] ?? ""}
                          </div>
                        )}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {table.getRowModel().rows.map((row) => (
                  <tr key={row.id}>
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id}>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </TableCell>
                    ))}
                  </tr>
                ))}
              </tbody>
            </BTable>
          </div>
        ) : (
          <div className="m-3 d-flex justify-content-center">
            {isLoading ? (
              <Spinner animation="border" role="status" size="sm">
                <span className="visually-hidden">Loading...</span>
              </Spinner>
            ) : (
              "There are no tasks that you are working on."
            )}
          </div>
        )}
      </Row>
      <div className="tw-flex tw-w-full tw-justify-center">
        {orders.length < totalCount ? (
          <Button
            className="tw-w-fit tw-px-4 tw-my-2 tw-self-center disabled:!tw-cursor-not-allowed disabled:tw-bg-gray-500 tw-border-none"
            size="sm"
            onClick={handleLoadMore}
            disabled={loadingMore}
          >
            {!loadingMore ? (
              "Load more"
            ) : (
              <>
                Loading{" "}
                <Spinner animation="border" role="status" size="sm"></Spinner>
              </>
            )}
          </Button>
        ) : null}
      </div>
      <Modal show={showModal} onHide={onCancel}>
        <Modal.Header closeButton>
          <Modal.Title>
            {createOrderStep === 1
              ? "Create Order - Step 1"
              : createOrderStep === 2
              ? "Create Order - Step 2"
              : createOrderStep === 3
              ? "Create Order - Step 3"
              : "Create Order - Step 4"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="tw-flex tw-flex-col tw-gap-2">
          {createOrderStep === 1 ? (
            <>
              <Form.Group className="mb-3">
                <Form.Label>Search for client:</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Search..."
                  value={searchQuery}
                  onChange={handleSearchChange}
                />
                {isSearching && (
                  <div className="mt-2 text-center">
                    <Spinner animation="border" size="sm" />
                  </div>
                )}
                {searchResults.length > 0 && (
                  <div className="mt-2 border rounded tw-max-h-[300px] tw-overflow-y-auto">
                    {searchResults.map((client) => (
                      <div
                        key={client.id}
                        className="p-2 hover:tw-bg-gray-100 cursor-pointer"
                        onClick={() => handleClientSelect(client)}
                      >
                        {client.username}
                      </div>
                    ))}
                  </div>
                )}
                {selectedClient && (
                  <div className="mt-2 p-2 border rounded bg-light tw-flex tw-justify-between tw-items-center">
                    <span>Selected: {selectedClient.username}</span>
                    <button
                      onClick={() => {
                        setSelectedClient(null);
                        if (searchQuery.length > 0) {
                          debouncedSearch(searchQuery);
                        }
                      }}
                      className="tw-bg-transparent tw-border-0 tw-text-gray-500 hover:tw-text-gray-700"
                    >
                      ✕
                    </button>
                  </div>
                )}
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Order type:</Form.Label>
                <div className="tw-flex tw-flex-col tw-gap-2">
                  <Form.Check
                    type="radio"
                    name="orderType"
                    id="player_analytics"
                    value="player_analytics"
                    label="Player Analytics"
                    checked={orderType === "player_analytics"}
                    onChange={handleOrderTypeChange}
                  />
                  <Form.Check
                    type="radio"
                    name="orderType"
                    id="team_analytics"
                    value="team_analytics"
                    label="Team Analytics"
                    checked={orderType === "team_analytics"}
                    onChange={handleOrderTypeChange}
                  />
                  <Form.Check
                    type="radio"
                    name="orderType"
                    id="team_analytics_pro"
                    value="team_analytics_pro"
                    label="Team Analytics Pro"
                    checked={orderType === "team_analytics_pro"}
                    onChange={handleOrderTypeChange}
                  />
                  <Form.Check
                    type="radio"
                    name="orderType"
                    id="match_analytics"
                    value="match_analytics"
                    label="Match Analytics"
                    checked={orderType === "match_analytics"}
                    onChange={handleOrderTypeChange}
                  />
                  <Form.Check
                    type="radio"
                    name="orderType"
                    id="match_analytics_pro"
                    value="match_analytics_pro"
                    label="Match Analytics Pro"
                    checked={orderType === "match_analytics_pro"}
                    onChange={handleOrderTypeChange}
                  />
                  <Form.Check
                    type="radio"
                    name="orderType"
                    id="training_analytics"
                    value="training_analytics"
                    label="Training Analytics"
                    checked={orderType === "training_analytics"}
                    onChange={handleOrderTypeChange}
                  />
                  <Form.Check
                    type="radio"
                    name="orderType"
                    id="broadcasting"
                    value="broadcasting"
                    label="Broadcasting"
                    checked={orderType === "broadcasting"}
                    onChange={handleOrderTypeChange}
                  />
                </div>
              </Form.Group>

              {["team_analytics", "team_analytics_pro"].includes(orderType) && (
                <>
                  <Form.Group className="mb-3">
                    <Form.Label>Order subtype:</Form.Label>
                    <div className="tw-flex tw-gap-4">
                      <Form.Check
                        type="radio"
                        name="orderSubtype"
                        id="5-a-side"
                        value="5-a-side"
                        label="5-a-side"
                        checked={orderSubtype === "5-a-side"}
                        onChange={(e) => setOrderSubtype(e.target.value)}
                      />
                      <Form.Check
                        type="radio"
                        name="orderSubtype"
                        id="8-a-side"
                        value="8-a-side"
                        label="8-a-side"
                        checked={orderSubtype === "8-a-side"}
                        onChange={(e) => setOrderSubtype(e.target.value)}
                      />
                      <Form.Check
                        type="radio"
                        name="orderSubtype"
                        id="11-a-side"
                        value="11-a-side"
                        label="11-a-side"
                        checked={orderSubtype === "11-a-side"}
                        onChange={(e) => setOrderSubtype(e.target.value)}
                      />
                    </div>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Teams amount:</Form.Label>
                    <div className="tw-flex tw-gap-4">
                      <Form.Check
                        type="radio"
                        name="teamsAmount"
                        id="oneTeam"
                        value={1}
                        label="1 Team"
                        checked={teamsAmount === 1}
                        onChange={(e) => setTeamsAmount(Number(e.target.value))}
                      />
                      <Form.Check
                        type="radio"
                        name="teamsAmount"
                        id="twoTeams"
                        value={2}
                        label="2 Teams"
                        checked={teamsAmount === 2}
                        onChange={(e) => setTeamsAmount(Number(e.target.value))}
                      />
                    </div>
                  </Form.Group>
                </>
              )}
            </>
          ) : createOrderStep === 2 ? (
            <>
              <Form.Group className="mb-3">
                <Form.Label>Assign Video:</Form.Label>
                <div>
                  <Form.Check
                    inline
                    type="radio"
                    name="assignVideo"
                    id="assignVideoNow"
                    value="now"
                    label="Now"
                    checked={assignVideo === "now"}
                    onChange={() => setAssignVideo("now")}
                  />
                  <Form.Check
                    inline
                    type="radio"
                    name="assignVideo"
                    id="assignVideoLater"
                    value="later"
                    label="Later"
                    checked={assignVideo === "later"}
                    onChange={() => setAssignVideo("later")}
                  />
                </div>
              </Form.Group>
              {orderType === "broadcasting" ? (
                <>
                  {isLoadingCameras && cameras.length === 0 ? (
                    <div className="mt-2 text-center">
                      <Spinner animation="border" size="sm" />
                    </div>
                  ) : (
                    <>
                      <Form.Group className="mb-3">
                        <Form.Label>Choose Arena:</Form.Label>
                        <Form.Select
                          value={selectedArena}
                          disabled={assignVideo === "later"}
                          onChange={(e) => {
                            setSelectedArena(e.target.value);
                            setTempSelectedCamera("");
                          }}
                        >
                          <option value="">-- Select Arena --</option>
                          {[
                            ...new Map(
                              cameras
                                .map((c) => [getArenaKey(c), c.arena_name])
                                .filter(([arenaId]) => arenaId != null)
                            ).entries(),
                          ].map(([arenaId, arenaName]) => (
                            <option key={arenaId} value={String(arenaId)}>
                              {arenaName}
                            </option>
                          ))}
                          <option value="no-arena">No Arena</option>
                        </Form.Select>
                      </Form.Group>
                      <Form.Group className="mb-3">
                        <Form.Label>Choose Field:</Form.Label>
                        <Form.Select
                          value={tempSelectedCamera}
                          onChange={(e) => setTempSelectedCamera(e.target.value)}
                          disabled={assignVideo === "later" || !selectedArena}
                        >
                          <option value="">-- Select Field --</option>
                          {cameras
                            .filter((c) =>
                              selectedArena === "no-arena"
                                ? !c.arena_name
                                : getArenaKey(c) === selectedArena
                            )
                            .map((c) => (
                              <option key={c.id} value={String(c.id)}>
                                {c.name}
                              </option>
                            ))}
                        </Form.Select>
                      </Form.Group>
                    </>
                  )}
                </>
              ) : (
                <Form.Group className="mb-3">
                  <Form.Label>Video URL:</Form.Label>
                  <Form.Control
                    type="url"
                    placeholder="Enter video URL"
                    value={link}
                    onChange={handleUrlChange}
                    disabled={assignVideo === "later"}
                    isInvalid={link.length > 0 && !isValidUrl}
                  />
                  {link.length > 0 && !isValidUrl && (
                    <Form.Control.Feedback type="invalid">
                      Please enter a valid URL
                    </Form.Control.Feedback>
                  )}
                </Form.Group>
              )}

              {orderType === "broadcasting" ? (
                <>
                  <Form.Group className="mb-3">
                    <Form.Label>Video Start:</Form.Label>
                    <Form.Control
                      type="datetime-local"
                      value={videoStart}
                      disabled={assignVideo === "later"}
                      onChange={(e) => setVideoStart(e.target.value)}
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Video End:</Form.Label>
                    <Form.Control
                      type="datetime-local"
                      value={videoEnd}
                      disabled={assignVideo === "later"}
                      onChange={(e) => setVideoEnd(e.target.value)}
                    />
                  </Form.Group>
                </>
              ) : (
                <Form.Group className="mb-3">
                  <Form.Label>
                    {orderType === "match_analytics_pro"
                      ? "Approx Video Stream Start (UTC time)"
                      : "Activity Date:"}
                  </Form.Label>
                  <Form.Control
                    type="datetime-local"
                    value={activityDate}
                    disabled={assignVideo === "later"}
                    onChange={(e) => setActivityDate(e.target.value)}
                  />
                </Form.Group>
              )}
            </>
          ) : createOrderStep === 3 ? (
            <>
              <Form.Group className="mb-3">
                <Form.Label>Camera Position:</Form.Label>
                <div>
                  <Form.Check
                    inline
                    type="radio"
                    name="cameraPosition"
                    id="regular"
                    value="regular"
                    label="Regular"
                    checked={cameraPosition === "regular"}
                    onChange={(e) => setCameraPosition(e.target.value)}
                  />
                  <Form.Check
                    inline
                    type="radio"
                    name="cameraPosition"
                    id="high"
                    value="high"
                    label="High"
                    checked={cameraPosition === "high"}
                    onChange={(e) => setCameraPosition(e.target.value)}
                  />
                  <Form.Check
                    inline
                    type="radio"
                    name="cameraPosition"
                    id="angle"
                    value="angle"
                    label="Angle"
                    checked={cameraPosition === "angle"}
                    onChange={(e) => setCameraPosition(e.target.value)}
                  />
                  <Form.Check
                    inline
                    type="radio"
                    name="cameraPosition"
                    id="behind_gates"
                    value="behind_gates"
                    label="Behind Gates"
                    checked={cameraPosition === "behind_gates"}
                    onChange={(e) => setCameraPosition(e.target.value)}
                  />
                </div>
              </Form.Group>
            </>
          ) : (
            // Step 2: Teams configuration
            <div className="tw-flex tw-flex-col tw-space-y-8">
              {tempOrderData?.activity_type === "training_analytics" ? (
                // Training Analytics - Single Training Team
                <div className="tw-space-y-4">
                  <div className="tw-text-lg tw-font-medium">Training team</div>

                  <div className="tw-flex tw-items-center tw-gap-4 tw-mb-3">
                    <div className="tw-w-32">Create new team:</div>
                    <Form.Control
                      type="text"
                      className="tw-flex-1"
                      placeholder="Type here..."
                      value={tempNewFirstTeam}
                      onChange={(e) => {
                        setTempNewFirstTeam(e.target.value);
                        // Clear selected team when typing new team name
                        if (e.target.value) {
                          setTempEditTeams({
                            ...tempEditTeams,
                            trainingTeam: {
                              ...tempEditTeams.trainingTeam,
                              name: "",
                            },
                          });
                        }
                      }}
                    />
                  </div>

                  <div className="tw-flex tw-items-center tw-gap-4 tw-mb-3">
                    <div className="tw-w-32">Choose team:</div>
                    <InfiniteScrollSelect
                      className="tw-flex-1"
                      options={coachTeams}
                      value={tempEditTeams.trainingTeam.name}
                      onChange={(e) =>
                        setTempEditTeams({
                          ...tempEditTeams,
                          trainingTeam: {
                            ...tempEditTeams.trainingTeam,
                            name: e.target.value,
                          },
                        })
                      }
                      disabled={!!tempNewFirstTeam}
                      hasMore={teamsHasMore}
                      isLoading={teamsLoading}
                      onLoadMore={loadMoreTeams}
                      placeholder="Select a team"
                      getOptionLabel={(team) => team?.name ?? ""}
                      getOptionValue={(team) => team?.name ?? ""}
                    />
                  </div>

                  <div className="tw-flex tw-items-center tw-gap-4">
                    <div className="tw-w-36">Choose team color:</div>
                    <Form.Select
                      className="tw-w-36"
                      value={tempEditTeams.trainingTeam.color}
                      onChange={(e) =>
                        setTempEditTeams({
                          ...tempEditTeams,
                          trainingTeam: {
                            ...tempEditTeams.trainingTeam,
                            color: e.target.value,
                          },
                        })
                      }
                    >
                      <option value="">Select color</option>
                      {TEAM_COLORS.map((color) => (
                        <option key={color.value} value={color.value}>
                          {color.label}
                        </option>
                      ))}
                    </Form.Select>
                    <Form.Check
                      type="checkbox"
                      label="Striped"
                      checked={tempEditTeams.trainingTeam.striped}
                      onChange={(e) =>
                        setTempEditTeams({
                          ...tempEditTeams,
                          trainingTeam: {
                            ...tempEditTeams.trainingTeam,
                            striped: e.target.checked,
                          },
                        })
                      }
                    />
                  </div>
                </div>
              ) : (
                // Regular order types - Home and Guest teams
                <>
                  {/* First Team Section */}
                  <div className="tw-space-y-4">
                    <div className="tw-text-lg tw-font-medium">
                      {tempOrderData?.activity_type === "match_analytics_pro"
                        ? "First team"
                        : "Home team"}
                    </div>

                    <div className="tw-flex tw-items-center tw-gap-4 tw-mb-3">
                      <div className="tw-w-32">Create new team:</div>
                      <Form.Control
                        type="text"
                        className="tw-flex-1"
                        placeholder="Type here..."
                        value={tempNewFirstTeam}
                        onChange={(e) => {
                          setTempNewFirstTeam(e.target.value);
                          // Clear selected team when typing new team name
                          if (e.target.value) {
                            setTempEditTeams({
                              ...tempEditTeams,
                              homeTeam: { ...tempEditTeams.homeTeam, name: "" },
                            });
                          }
                        }}
                      />
                    </div>

                    <div className="tw-flex tw-items-center tw-gap-4 tw-mb-3">
                      <div className="tw-w-32">Choose team:</div>
                      <InfiniteScrollSelect
                        className="tw-flex-1"
                        options={coachTeams}
                        value={tempEditTeams.homeTeam.name}
                        onChange={(e) =>
                          setTempEditTeams({
                            ...tempEditTeams,
                            homeTeam: {
                              ...tempEditTeams.homeTeam,
                              name: e.target.value,
                            },
                          })
                        }
                        disabled={!!tempNewFirstTeam}
                        hasMore={teamsHasMore}
                        isLoading={teamsLoading}
                        onLoadMore={loadMoreTeams}
                        placeholder="Select a team"
                        getOptionLabel={(team) => team?.name ?? ""}
                        getOptionValue={(team) => team?.name ?? ""}
                      />
                    </div>

                    <div className="tw-flex tw-items-center tw-gap-4">
                      <div className="tw-w-36">Choose team color:</div>
                      <Form.Select
                        className="tw-w-36"
                        value={tempEditTeams.homeTeam.color}
                        onChange={(e) =>
                          setTempEditTeams({
                            ...tempEditTeams,
                            homeTeam: {
                              ...tempEditTeams.homeTeam,
                              color: e.target.value,
                            },
                          })
                        }
                      >
                        <option value="">Select color</option>
                        {TEAM_COLORS.map((color) => (
                          <option key={color.value} value={color.value}>
                            {color.label}
                          </option>
                        ))}
                      </Form.Select>
                      <Form.Check
                        type="checkbox"
                        label="Striped"
                        checked={tempEditTeams.homeTeam.striped}
                        onChange={(e) =>
                          setTempEditTeams({
                            ...tempEditTeams,
                            homeTeam: {
                              ...tempEditTeams.homeTeam,
                              striped: e.target.checked,
                            },
                          })
                        }
                      />
                    </div>
                  </div>

                  <hr />

                  {/* Second Team Section */}
                  <div className="tw-space-y-4">
                    <div className="tw-text-lg tw-font-medium">
                      {tempOrderData?.activity_type === "match_analytics_pro"
                        ? "Second team"
                        : "Guest team"}
                    </div>

                    <div className="tw-flex tw-items-center tw-gap-4 tw-mb-3">
                      <div className="tw-w-32">Create new team:</div>
                      <Form.Control
                        type="text"
                        className="tw-flex-1"
                        placeholder="Type here..."
                        value={tempNewSecondTeam}
                        onChange={(e) => {
                          setTempNewSecondTeam(e.target.value);
                          // Clear selected team when typing new team name
                          if (e.target.value) {
                            setTempEditTeams({
                              ...tempEditTeams,
                              guestTeam: {
                                ...tempEditTeams.guestTeam,
                                name: "",
                              },
                            });
                          }
                        }}
                      />
                    </div>

                    <div className="tw-flex tw-items-center tw-gap-4 tw-mb-3">
                      <div className="tw-w-32">Choose team:</div>
                      <InfiniteScrollSelect
                        className="tw-flex-1"
                        options={coachTeams}
                        value={tempEditTeams.guestTeam.name}
                        onChange={(e) =>
                          setTempEditTeams({
                            ...tempEditTeams,
                            guestTeam: {
                              ...tempEditTeams.guestTeam,
                              name: e.target.value,
                            },
                          })
                        }
                        disabled={!!tempNewSecondTeam}
                        hasMore={teamsHasMore}
                        isLoading={teamsLoading}
                        onLoadMore={loadMoreTeams}
                        placeholder="Select a team"
                        getOptionLabel={(team) => team?.name ?? ""}
                        getOptionValue={(team) => team?.name ?? ""}
                      />
                    </div>

                    <div className="tw-flex tw-items-center tw-gap-4">
                      <div className="tw-w-36">Choose team color:</div>
                      <Form.Select
                        className="tw-w-36"
                        value={tempEditTeams.guestTeam.color}
                        onChange={(e) =>
                          setTempEditTeams({
                            ...tempEditTeams,
                            guestTeam: {
                              ...tempEditTeams.guestTeam,
                              color: e.target.value,
                            },
                          })
                        }
                      >
                        <option value="">Select color</option>
                        {TEAM_COLORS.map((color) => (
                          <option key={color.value} value={color.value}>
                            {color.label}
                          </option>
                        ))}
                      </Form.Select>
                      <Form.Check
                        type="checkbox"
                        label="Striped"
                        checked={tempEditTeams.guestTeam.striped}
                        onChange={(e) =>
                          setTempEditTeams({
                            ...tempEditTeams,
                            guestTeam: {
                              ...tempEditTeams.guestTeam,
                              striped: e.target.checked,
                            },
                          })
                        }
                      />
                    </div>
                  </div>

                  <hr />

                  {/* Order Team Section - Only show for non-training orders */}
                  {tempOrderData?.activity_type !== "training_analytics" && (
                    <div className="tw-space-y-4">
                      <div className="tw-text-lg tw-font-medium">
                        Order team
                      </div>
                      <Form.Select
                        className="tw-w-full"
                        disabled={
                          tempOrderData?.activity_type === "match_analytics_pro"
                        }
                        value={
                          tempOrderData?.activity_type === "match_analytics_pro"
                            ? "both"
                            : tempEditTeams.orderTeam
                        }
                        onChange={(e) =>
                          setTempEditTeams({
                            ...tempEditTeams,
                            orderTeam: e.target.value,
                          })
                        }
                      >
                        <option value="" disabled>
                          Select team
                        </option>
                        <option value="home_team">Home team</option>
                        <option value="guest_team">Guest team</option>
                        <option value="both">Both</option>
                      </Form.Select>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button type="button" variant="secondary" onClick={onCancel}>
            Cancel
          </Button>
          {createOrderStep === 1 ? (
            <Button
              type="button"
              variant="primary"
              onClick={onNext}
              disabled={
                !orderType ||
                !selectedClient ||
                (["team_analytics", "team_analytics_pro"].includes(orderType) &&
                  !orderSubtype)
              }
            >
              Next
            </Button>
          ) : createOrderStep === 2 ? (
            <>
              <Button type="button" variant="secondary" onClick={onBack}>
                Back
              </Button>
              <Button
                type="button"
                variant="primary"
                onClick={onNext}
                disabled={
                  assignVideo !== "later" && (
                    (orderType === "broadcasting" && (!tempSelectedCamera || !videoStart || !videoEnd)) ||
                    (orderType !== "broadcasting" && (!link || (link && !isValidUrl) || !activityDate))
                  )
                }
              >
                Next
              </Button>
            </>
          ) : createOrderStep === 3 ? (
            <>
              <Button type="button" variant="secondary" onClick={onBack}>
                Back
              </Button>
              <Button
                type="button"
                variant="primary"
                onClick={onNext}
                disabled={!cameraPosition}
              >
                Next
              </Button>
            </>
          ) : (
            <>
              <Button type="button" variant="secondary" onClick={onBack}>
                Back
              </Button>
              <Button
                type="button"
                variant="success"
                onClick={onCreate}
                disabled={
                  isCreating ||
                  (tempOrderData?.activity_type === "training_analytics"
                    ? // For training analytics, only validate training team
                      (!tempEditTeams.trainingTeam.name && !tempNewFirstTeam) ||
                      !tempEditTeams.trainingTeam.color
                    : // For other order types, validate home and guest teams
                      (!tempEditTeams.homeTeam.name && !tempNewFirstTeam) ||
                      (!tempEditTeams.guestTeam.name && !tempNewSecondTeam) ||
                      !tempEditTeams.homeTeam.color ||
                      !tempEditTeams.guestTeam.color ||
                      (tempOrderData?.activity_type !== "match_analytics_pro" &&
                        !tempEditTeams.orderTeam))
                }
              >
                {!isCreating ? (
                  "Create"
                ) : (
                  <>
                    Creating{" "}
                    <Spinner
                      animation="border"
                      role="status"
                      size="sm"
                    ></Spinner>
                  </>
                )}
              </Button>
            </>
          )}
        </Modal.Footer>
      </Modal>
      <Modal
        show={showTeamsModal}
        onHide={() => {
          setShowTeamsModal(false);
          setCoachTeams([]);
          setTeamsPage(1);
          setTeamsHasMore(false);
          setTeamsCount(0);
        }}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            The teams for Owner {editingOrder?.owner_name} and Order{" "}
            {editingOrder?.id}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="tw-flex tw-flex-col tw-space-y-8">
          {editingOrder?.order_type === "training_analytics" ? (
            // Training Analytics - Single Training Team
            <div className="tw-space-y-4">
              <div className="tw-text-lg tw-font-medium">Training team</div>

              <div className="tw-flex tw-items-center tw-gap-4 tw-mb-3">
                <div className="tw-w-32">Create new team:</div>
                <Form.Control
                  type="text"
                  className="tw-flex-1"
                  placeholder="Type here..."
                  value={newTrainingTeam}
                  onChange={(e) => {
                    setNewTrainingTeam(e.target.value);
                    // Clear selected team when typing new team name
                    if (e.target.value) {
                      setEditTeams({
                        ...editTeams,
                        trainingTeam: { ...editTeams.trainingTeam, name: "" },
                      });
                    }
                  }}
                />
              </div>

              <div className="tw-flex tw-items-center tw-gap-4 tw-mb-3">
                <div className="tw-w-32">Choose team:</div>
                <InfiniteScrollSelect
                  className="tw-flex-1"
                  options={coachTeams}
                  value={editTeams.trainingTeam.name}
                  onChange={(e) =>
                    setEditTeams({
                      ...editTeams,
                      trainingTeam: {
                        ...editTeams.trainingTeam,
                        name: e.target.value,
                      },
                    })
                  }
                  disabled={!!newTrainingTeam}
                  hasMore={teamsHasMore}
                  isLoading={teamsLoading}
                  onLoadMore={loadMoreTeams}
                  placeholder="Select a team"
                  getOptionLabel={(team) => team?.name ?? ""}
                  getOptionValue={(team) => team?.name ?? ""}
                />
              </div>

              <div className="tw-flex tw-items-center tw-gap-4">
                <div className="tw-w-36">Choose team color:</div>
                <Form.Select
                  className="tw-w-36"
                  value={editTeams.trainingTeam.color}
                  onChange={(e) =>
                    setEditTeams({
                      ...editTeams,
                      trainingTeam: {
                        ...editTeams.trainingTeam,
                        color: e.target.value,
                      },
                    })
                  }
                >
                  <option value="">Select color</option>
                  {TEAM_COLORS.map((color) => (
                    <option key={color.value} value={color.value}>
                      {color.label}
                    </option>
                  ))}
                </Form.Select>
                <Form.Check
                  type="checkbox"
                  label="Striped"
                  checked={editTeams.trainingTeam.striped}
                  onChange={(e) =>
                    setEditTeams({
                      ...editTeams,
                      trainingTeam: {
                        ...editTeams.trainingTeam,
                        striped: e.target.checked,
                      },
                    })
                  }
                />
              </div>
            </div>
          ) : (
            // Regular order types - Home and Guest teams
            <>
              {/* First Team Section */}
              <div className="tw-space-y-4">
                <div className="tw-text-lg tw-font-medium">
                  {editingOrder?.order_type === "match_analytics_pro"
                    ? "First team"
                    : "Home team"}
                </div>

                <div className="tw-flex tw-items-center tw-gap-4 tw-mb-3">
                  <div className="tw-w-32">Create new team:</div>
                  <Form.Control
                    type="text"
                    className="tw-flex-1"
                    placeholder="Type here..."
                    value={newFirstTeam}
                    onChange={(e) => {
                      setNewFirstTeam(e.target.value);
                      // Clear selected team when typing new team name
                      if (e.target.value) {
                        setEditTeams({
                          ...editTeams,
                          homeTeam: { ...editTeams.homeTeam, name: "" },
                        });
                      }
                    }}
                  />
                </div>

                <div className="tw-flex tw-items-center tw-gap-4 tw-mb-3">
                  <div className="tw-w-32">Choose team:</div>
                  <InfiniteScrollSelect
                    className="tw-flex-1"
                    options={coachTeams}
                    value={editTeams.homeTeam.name}
                    onChange={(e) =>
                      setEditTeams({
                        ...editTeams,
                        homeTeam: {
                          ...editTeams.homeTeam,
                          name: e.target.value,
                        },
                      })
                    }
                    disabled={!!newFirstTeam}
                    hasMore={teamsHasMore}
                    isLoading={teamsLoading}
                    onLoadMore={loadMoreTeams}
                    placeholder="Select a team"
                    getOptionLabel={(team) => team?.name ?? ""}
                    getOptionValue={(team) => team?.name ?? ""}
                  />
                </div>

                <div className="tw-flex tw-items-center tw-gap-4">
                  <div className="tw-w-36">Choose team color:</div>
                  <Form.Select
                    className="tw-w-36"
                    value={editTeams.homeTeam.color}
                    onChange={(e) =>
                      setEditTeams({
                        ...editTeams,
                        homeTeam: {
                          ...editTeams.homeTeam,
                          color: e.target.value,
                        },
                      })
                    }
                  >
                    <option value="">Select color</option>
                    {TEAM_COLORS.map((color) => (
                      <option key={color.value} value={color.value}>
                        {color.label}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Check
                    type="checkbox"
                    label="Striped"
                    checked={editTeams.homeTeam.striped}
                    onChange={(e) =>
                      setEditTeams({
                        ...editTeams,
                        homeTeam: {
                          ...editTeams.homeTeam,
                          striped: e.target.checked,
                        },
                      })
                    }
                  />
                </div>
              </div>

              <hr />

              {/* Second Team Section */}
              <div className="tw-space-y-4">
                <div className="tw-text-lg tw-font-medium">
                  {editingOrder?.order_type === "match_analytics_pro"
                    ? "Second team"
                    : "Guest team"}
                </div>

                <div className="tw-flex tw-items-center tw-gap-4 tw-mb-3">
                  <div className="tw-w-32">Create new team:</div>
                  <Form.Control
                    type="text"
                    className="tw-flex-1"
                    placeholder="Type here..."
                    value={newSecondTeam}
                    onChange={(e) => {
                      setNewSecondTeam(e.target.value);
                      // Clear selected team when typing new team name
                      if (e.target.value) {
                        setEditTeams({
                          ...editTeams,
                          guestTeam: { ...editTeams.guestTeam, name: "" },
                        });
                      }
                    }}
                  />
                </div>

                <div className="tw-flex tw-items-center tw-gap-4 tw-mb-3">
                  <div className="tw-w-32">Choose team:</div>
                  <InfiniteScrollSelect
                    className="tw-flex-1"
                    options={coachTeams}
                    value={editTeams.guestTeam.name}
                    onChange={(e) =>
                      setEditTeams({
                        ...editTeams,
                        guestTeam: {
                          ...editTeams.guestTeam,
                          name: e.target.value,
                        },
                      })
                    }
                    disabled={!!newSecondTeam}
                    hasMore={teamsHasMore}
                    isLoading={teamsLoading}
                    onLoadMore={loadMoreTeams}
                    placeholder="Select a team"
                    getOptionLabel={(team) => team?.name ?? ""}
                    getOptionValue={(team) => team?.name ?? ""}
                  />
                </div>

                <div className="tw-flex tw-items-center tw-gap-4">
                  <div className="tw-w-36">Choose team color:</div>
                  <Form.Select
                    className="tw-w-36"
                    value={editTeams.guestTeam.color}
                    onChange={(e) =>
                      setEditTeams({
                        ...editTeams,
                        guestTeam: {
                          ...editTeams.guestTeam,
                          color: e.target.value,
                        },
                      })
                    }
                  >
                    <option value="">Select color</option>
                    {TEAM_COLORS.map((color) => (
                      <option key={color.value} value={color.value}>
                        {color.label}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Check
                    type="checkbox"
                    label="Striped"
                    checked={editTeams.guestTeam.striped}
                    onChange={(e) =>
                      setEditTeams({
                        ...editTeams,
                        guestTeam: {
                          ...editTeams.guestTeam,
                          striped: e.target.checked,
                        },
                      })
                    }
                  />
                </div>
              </div>

              <hr />

              {/* Order Team Section - Only show for non-training orders */}
              {editingOrder?.order_type !== "training_analytics" && (
                <div className="tw-space-y-4">
                  <div className="tw-text-lg tw-font-medium">Order team</div>
                  <Form.Select
                    className="tw-w-full"
                    disabled={
                      editingOrder?.order_type === "match_analytics_pro"
                    }
                    value={
                      editingOrder?.order_type === "match_analytics_pro"
                        ? "both"
                        : editTeams.orderTeam
                    }
                    onChange={(e) =>
                      setEditTeams({
                        ...editTeams,
                        orderTeam: e.target.value,
                      })
                    }
                  >
                    <option value="" disabled>
                      Select team
                    </option>
                    <option value="home_team">Home team</option>
                    <option value="guest_team">Guest team</option>
                    <option value="both">Both</option>
                  </Form.Select>
                </div>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="primary"
            className="tw-border-none tw-bg-blue-500 tw-px-8 disabled:tw-bg-gray-400 disabled:tw-cursor-not-allowed disabled:tw-opacity-75"
            disabled={
              editingOrder?.order_type === "training_analytics"
                ? // For training analytics, only validate training team
                  (!editTeams.trainingTeam.name && !newTrainingTeam) ||
                  !editTeams.trainingTeam.color
                : // For other order types, validate home and guest teams
                  (!editTeams.homeTeam.name && !newFirstTeam) ||
                  (!editTeams.guestTeam.name && !newSecondTeam) ||
                  !editTeams.homeTeam.color ||
                  !editTeams.guestTeam.color ||
                  (editingOrder?.order_type !== "match_analytics_pro" &&
                    !editTeams.orderTeam)
            }
            onClick={async () => {
              try {
                let teamsData;

                if (editingOrder?.order_type === "training_analytics") {
                  // For training analytics, use training team data
                  teamsData = {
                    home_team: {
                      id: newTrainingTeam
                        ? null
                        : coachTeams.find(
                            (team) => team.name === editTeams.trainingTeam.name
                          )?.id,
                      name: newTrainingTeam || editTeams.trainingTeam.name,
                      color: editTeams.trainingTeam.color,
                      striped: editTeams.trainingTeam.striped,
                    },
                    guest_team: null, // No guest team for training
                    order_team: null, // No order team for training
                  };
                } else {
                  // For other order types, use regular home/guest team data
                  teamsData = {
                    home_team: {
                      id: newFirstTeam
                        ? null
                        : coachTeams.find(
                            (team) => team.name === editTeams.homeTeam.name
                          )?.id,
                      name: newFirstTeam || editTeams.homeTeam.name,
                      color: editTeams.homeTeam.color,
                      striped: editTeams.homeTeam.striped,
                    },
                    guest_team: {
                      id: newSecondTeam
                        ? null
                        : coachTeams.find(
                            (team) => team.name === editTeams.guestTeam.name
                          )?.id,
                      name: newSecondTeam || editTeams.guestTeam.name,
                      color: editTeams.guestTeam.color,
                      striped: editTeams.guestTeam.striped,
                    },
                    order_team:
                      editingOrder?.order_type === "match_analytics_pro"
                        ? "both"
                        : editTeams.orderTeam,
                  };
                }

                await ordersApi.updateOrderTeams(editingOrder.id, teamsData);
                const updatedOrders = await ordersApi.getOrdersList();
                if (!updatedOrders) return;
                setOrders(updatedOrders?.results);

                // Clear all modal state
                setShowTeamsModal(false);
                setEditingOrder(null);
                setEditTeams({
                  homeTeam: { name: "", color: "", striped: false },
                  guestTeam: { name: "", color: "", striped: false },
                  orderTeam: "",
                  trainingTeam: { name: "", color: "", striped: false },
                });
                setNewFirstTeam("");
                setNewSecondTeam("");
                setNewTrainingTeam("");
                setCoachTeams([]);
                setTeamsPage(1);
                setTeamsHasMore(false);
                setTeamsCount(0);

                toast.success("Teams updated successfully");
              } catch (err) {
                toast.error("Failed to update teams");
              }
            }}
          >
            SAVE
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showStatsModal}
        onHide={() => {
          setShowStatsModal(false);
          setIsEditingStats(false);
          setEditedStats(null);
        }}
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {isEditingStats ? "Edit Stats" : "View Stats"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Control
            as="textarea"
            rows={10}
            value={isEditingStats ? editedStats : formatStats(selectedStats)}
            readOnly={!isEditingStats}
            onChange={(e) => {
              if (isEditingStats) {
                e.stopPropagation(); // Prevent event bubbling
                setEditedStats(e.target.value);
              }
            }}
          />
        </Modal.Body>
        <Modal.Footer>
          {isEditingStats ? (
            <>
              <Button
                variant="primary"
                size="sm"
                onClick={async () => {
                  try {
                    // Parse the edited stats to validate JSON
                    const parsedStats = JSON.parse(editedStats);

                    // Update the stats in the database
                    await ordersApi.updateOrderStats(
                      editingOrder?.id,
                      parsedStats
                    );

                    // Refresh orders list to get updated stats
                    const updatedOrders = await ordersApi.getOrdersList();
                    if (!updatedOrders) return;
                    setOrders(updatedOrders?.results);
                    setTotalCount(updatedOrders?.count);

                    // Exit edit mode
                    setIsEditingStats(false);
                    setEditedStats(null);
                    setShowStatsModal(false);

                    toast.success("Stats updated successfully");
                  } catch (err) {
                    toast.error(
                      err.response?.data?.message ||
                        "Failed to update stats. Please check your JSON format."
                    );
                  }
                }}
              >
                Save
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  setIsEditingStats(false);
                  setEditedStats(null);
                }}
              >
                Cancel
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="primary"
                size="sm"
                onClick={() => {
                  if (editingOrder) {
                    const calcStats = async () => {
                      try {
                        await ordersApi.updateOrderStats(editingOrder.id, null);
                        const updatedOrders = await ordersApi.getOrdersList();
                        if (!updatedOrders) return;
                        setOrders(updatedOrders?.results);
                        setTotalCount(updatedOrders?.count);
                        toast.success(
                          "Successfully started stats calculation."
                        );
                      } catch (err) {
                        toast.error(
                          err.response?.data?.message ||
                            "Failed to start stats calculation."
                        );
                      }
                    };
                    calcStats();
                  }
                }}
              >
                Recalc stats
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={() => {
                  setIsEditingStats(true);
                  // Use the complete stats object for editing, not the filtered one
                  setEditedStats(JSON.stringify(selectedStats, null, 2));
                }}
              >
                Edit
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setShowStatsModal(false)}
              >
                Close
              </Button>
            </>
          )}
        </Modal.Footer>
      </Modal>

      {/* Match Protocol Modal */}
      <Modal
        show={showProtocolModal}
        onHide={() => {
          setShowProtocolModal(false);
          setProtocolData(null);
        }}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Match Protocol</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {protocolLoading ? (
            <div className="tw-flex tw-justify-center tw-py-8">
              <Spinner animation="border" role="status" />
            </div>
          ) : protocolData ? (
            <div className="tw-space-y-6">
              {[
                { key: "home_team", label: "Home" },
                { key: "guest_team", label: "Guest" },
              ].map(({ key, label }) => {
                const team = protocolData[key];
                if (!team) return null;
                return (
                  <div key={key}>
                    <h5 className="tw-font-bold tw-mb-2">
                      {label}: {team.name}
                    </h5>

                    <p className="tw-text-sm tw-font-semibold tw-mb-1">
                      Starting Lineup
                    </p>
                    <table className="table table-sm table-bordered tw-mb-3">
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Name</th>
                          <th>Goals</th>
                          <th>Assists</th>
                          <th>YC</th>
                          <th>RC</th>
                          <th>Saves</th>
                          <th>Strokes</th>
                          <th>Recoveries</th>
                          <th>Sub</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(team.start_lineup || []).map((p, i) => (
                          <tr key={i}>
                            <td>{p.jersey}</td>
                            <td>
                              {p.first_name} {p.last_name}
                            </td>
                            <td>{p.goals || ""}</td>
                            <td>{p.assists || ""}</td>
                            <td>{p.yellow_cards || ""}</td>
                            <td>{p.red_cards || ""}</td>
                            <td>{p.saves || ""}</td>
                            <td>
                              {p.successful_strokes || 0}/{p.strokes || 0}
                            </td>
                            <td>{p.recoveries || ""}</td>
                            <td>
                              {p.replaced_at
                                ? `out ${p.replaced_at}'`
                                : p.came_on_at
                                ? `in ${p.came_on_at}'`
                                : ""}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>

                    <p className="tw-text-sm tw-font-semibold tw-mb-1">
                      Substitutions
                    </p>
                    <table className="table table-sm table-bordered">
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Name</th>
                          <th>Goals</th>
                          <th>Assists</th>
                          <th>YC</th>
                          <th>RC</th>
                          <th>Saves</th>
                          <th>Strokes</th>
                          <th>Recoveries</th>
                          <th>Sub</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(team.substitutions || []).map((p, i) => (
                          <tr key={i}>
                            <td>{p.jersey}</td>
                            <td>
                              {p.first_name} {p.last_name}
                            </td>
                            <td>{p.goals || ""}</td>
                            <td>{p.assists || ""}</td>
                            <td>{p.yellow_cards || ""}</td>
                            <td>{p.red_cards || ""}</td>
                            <td>{p.saves || ""}</td>
                            <td>
                              {p.successful_strokes || 0}/{p.strokes || 0}
                            </td>
                            <td>{p.recoveries || ""}</td>
                            <td>
                              {p.came_on_at
                                ? `in ${p.came_on_at}'`
                                : ""}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="tw-text-center tw-text-gray-500">
              No protocol data available.
            </p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="primary"
            disabled={
              protocolUpdating ||
              protocolLoading ||
              !protocolData?.external_id
            }
            onClick={async () => {
              const externalId = protocolData?.external_id;
              if (!externalId || !protocolActivityId) return;
              try {
                setProtocolUpdating(true);
                await ordersApi.syncMatchProtocol(externalId);
                const data = await ordersApi.getMatchProtocol(
                  protocolActivityId
                );
                setProtocolData(data);
                toast.success("Protocol updated.");
              } catch (err) {
                console.log(err);
                toast.error(
                  err.response?.data?.message || "Failed to update protocol."
                );
              } finally {
                setProtocolUpdating(false);
              }
            }}
          >
            {protocolUpdating ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  className="tw-mr-2"
                />
                Updating...
              </>
            ) : (
              "Update protocol"
            )}
          </Button>
          <Button
            variant="secondary"
            onClick={() => {
              setShowProtocolModal(false);
              setProtocolData(null);
              setProtocolActivityId(null);
            }}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Change Order Type Modal */}
      <Modal show={showChangeTypeModal} onHide={() => setShowChangeTypeModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Change Order Type (ID: {changeTypeOrder?.id})</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="tw-mb-3">
            <Form.Label>Order Type</Form.Label>
            <Form.Select
              value={changeTypeValue}
              onChange={(e) => setChangeTypeValue(e.target.value)}
            >
              <option value="">Select type...</option>
              <option value="player_analytics">Player analytics</option>
              <option value="team_analytics">Team analytics</option>
              <option value="team_analytics_pro">Team analytics pro</option>
              <option value="match_analytics">Match analytics</option>
              <option value="match_analytics_pro">Match analytics pro</option>
              <option value="training_analytics">Training analytics</option>
              <option value="broadcasting">Broadcasting</option>
              <option value="video_recording">Video recording</option>
            </Form.Select>
          </Form.Group>
          <Form.Group className="tw-mb-3">
            <Form.Label>Order Subtype</Form.Label>
            <Form.Select
              value={changeSubtypeValue}
              onChange={(e) => setChangeSubtypeValue(e.target.value)}
            >
              <option value="">None</option>
              <option value="player">Player</option>
              <option value="11-a-side">11 a side</option>
              <option value="8-a-side">8 a side</option>
              <option value="5-a-side">5 a side</option>
            </Form.Select>
          </Form.Group>
          {changeTypeOrder && (
            <div className="tw-text-sm tw-text-gray-500">
              Current: {changeTypeOrder.order_type}
              {changeTypeOrder.order_subtype ? ` / ${changeTypeOrder.order_subtype}` : ""}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowChangeTypeModal(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={!changeTypeValue || isChangingType}
            onClick={async () => {
              try {
                setIsChangingType(true);
                const payload = { order_type: changeTypeValue };
                if (changeSubtypeValue) payload.order_subtype = changeSubtypeValue;
                await ordersApi.updateOrderType(changeTypeOrder.id, payload);
                const updatedOrders = await ordersApi.getOrdersList(pageNum, orderSearchQuery);
                if (updatedOrders) {
                  setOrders(updatedOrders.results);
                  setTotalCount(updatedOrders.count);
                }
                setShowChangeTypeModal(false);
                toast.success("Order type updated successfully.");
              } catch (err) {
                toast.error(
                  err.response?.data?.error ||
                    err.response?.data?.message ||
                    "Failed to update order type."
                );
              } finally {
                setIsChangingType(false);
              }
            }}
          >
            {isChangingType ? (
              <>Saving <Spinner animation="border" size="sm" /></>
            ) : (
              "Save"
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Cancel Order Confirmation Modal */}
      <Modal show={showCancelModal} onHide={() => setShowCancelModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Cancel Order</Modal.Title>
        </Modal.Header>
        <Modal.Body className="tw-flex-col">
          <p>
            Do you really want to cancel the order({orderToCancel?.id})? If it's
            paid the money will be returned
          </p>
          <Form.Group className="tw-mb-3">
            <Form.Label>Reason for cancellation (optional):</Form.Label>
            <Form.Select
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              className="tw-mb-2"
            >
              <option value="">Select a reason...</option>
              <option value="Unable to download video">
                Unable to download video
              </option>
              <option value="Incorrect order type selected">
                Incorrect order type selected
              </option>
              <option value="Incorrect match type selected (number of players or format)">
                Incorrect match type selected (number of players or format)
              </option>
              <option value="Unable to identify the ordered player">
                Unable to identify the ordered player
              </option>
            </Form.Select>
            <Form.Control
              as="textarea"
              rows={3}
              placeholder="Or enter additional details here..."
              value={cancelReasonText}
              onChange={(e) => setCancelReasonText(e.target.value)}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setShowCancelModal(false);
              setCancelReason("");
              setCancelReasonText("");
            }}
          >
            NO
          </Button>
          <Button
            className="tw-rounded-md"
            variant="danger"
            onClick={async () => {
              try {
                setIsCancelling(true);
                // Combine selected reason and textarea content
                const combinedReason = cancelReason
                  ? cancelReasonText
                    ? `${cancelReason}: ${cancelReasonText}`
                    : cancelReason
                  : cancelReasonText;

                await ordersApi.cancelOrder(orderToCancel.id, {
                  cancel_reason: combinedReason,
                });

                // Update orders list to reflect changes
                const updatedOrders = await ordersApi.getOrdersList();
                if (!updatedOrders) return;
                setOrders(updatedOrders?.results);
                setTotalCount(updatedOrders?.count);

                setShowCancelModal(false);
                setOrderToCancel(null);
                setCancelReason("");
                setCancelReasonText("");
                toast.success("Order cancelled successfully.");
              } catch (err) {
                console.log(err);
                toast.error(
                  err.response?.data?.message ||
                    JSON.stringify(err.response?.data)?.slice(0, 200) ||
                    err.message ||
                    "Something went wrong while cancelling the order."
                );
              } finally {
                setIsCancelling(false);
              }
            }}
            disabled={isCancelling}
          >
            {isCancelling ? (
              <>
                Cancelling{" "}
                <Spinner animation="border" role="status" size="sm"></Spinner>
              </>
            ) : (
              "Cancel order"
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Camera Position Modal */}
      <Modal show={showCameraModal} onHide={() => setShowCameraModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Update Camera Position</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Camera Position:</Form.Label>
            <Form.Select
              value={selectedCameraPosition}
              onChange={(e) => setSelectedCameraPosition(e.target.value)}
            >
              <option value="">Select camera position</option>
              <option value="regular">Regular</option>
              <option value="high">High</option>
              <option value="angle">Angle</option>
              <option value="behind_gates">Behind Gates</option>
            </Form.Select>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCameraModal(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={async () => {
              try {
                setIsUpdatingCamera(true);
                await ordersApi.updateCameraPosition(editingCameraOrder.id, {
                  camera_position: selectedCameraPosition,
                });

                // Update orders list to reflect hanges
                const updatedOrders = await ordersApi.getOrdersList();
                if (!updatedOrders) return;
                setOrders(updatedOrders?.results);
                setTotalCount(updatedOrders?.count);

                setShowCameraModal(false);
                setEditingCameraOrder(null);
                setSelectedCameraPosition("");
                toast.success("Camera position updated successfully.");
              } catch (err) {
                console.log(err);
                toast.error(
                  err.response?.data?.message ||
                    JSON.stringify(err.response?.data)?.slice(0, 200) ||
                    err.message ||
                    "Something went wrong while updating camera position."
                );
              } finally {
                setIsUpdatingCamera(false);
              }
            }}
            disabled={isUpdatingCamera || !selectedCameraPosition}
          >
            {isUpdatingCamera ? (
              <>
                Updating{" "}
                <Spinner animation="border" role="status" size="sm"></Spinner>
              </>
            ) : (
              "Save"
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showMatchesModal} onHide={() => setShowMatchesModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Order {matchesOrderId} matches</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {matchesLoading ? (
            <div
              className="tw-flex tw-justify-center tw-items-center"
              style={{ minHeight: 100 }}
            >
              <Spinner animation="border" />
            </div>
          ) : orderMatches.length === 0 ? (
            <div>No matches found.</div>
          ) : (
            <div>
              {orderMatches.map((match) => (
                <div
                  key={match.id}
                  className="tw-flex tw-items-center tw-gap-4 tw-mb-2"
                >
                  <span style={{ minWidth: 50 }}>{match.id}</span>
                  <span className="tw-flex tw-gap-1">
                    <span
                      className="tw-w-5 tw-h-5 tw-inline-block tw-rounded tw-border tw-border-solid tw-border-gray-400"
                      style={{ background: getTeamColorValue(match.left_team_jersey_color) }}
                    ></span>
                    <span
                      className="tw-w-5 tw-h-5 tw-inline-block tw-rounded tw-border tw-border-solid tw-border-gray-400"
                      style={{ background: getTeamColorValue(match.right_team_jersey_color) }}
                    ></span>
                  </span>
                  {match.order_id !== null ? (
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={() => handleUnbindMatch(match.id)}
                      className="mx-1"
                      disabled={bindUnbindLoading[match.id]}
                    >
                      Unbind
                    </Button>
                  ) : (
                    <Button
                      variant="outline-success"
                      size="sm"
                      onClick={() => handleBindMatch(match.id)}
                      className="mx-1"
                      disabled={bindUnbindLoading[match.id]}
                    >
                      Bind
                    </Button>
                  )}
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    className="mx-1"
                    onClick={() => {
                      setEditingMatch(match);
                      setEditTeams({
                        homeTeam: { color: match.left_team_jersey_color },
                        guestTeam: { color: match.right_team_jersey_color },
                      });
                      setShowColorModal(true);
                    }}
                  >
                    Edit colors
                  </Button>
                </div>
              ))}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowMatchesModal(false)}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showActivitySetupModal}
        onHide={() => setShowActivitySetupModal(false)}
      >
        <Modal.Header closeButton>
          <Modal.Title>
            Activity setup
            {activitySetupOrder ? ` (order ${activitySetupOrder.id})` : ""}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loadingActivitySetup ? (
            <div
              className="tw-flex tw-justify-center tw-items-center"
              style={{ minHeight: 100 }}
            >
              <Spinner animation="border" />
            </div>
          ) : (
            <div className="tw-flex tw-flex-col tw-gap-4">
              <div className="tw-mb-4 tw-flex tw-items-center tw-gap-3">
                <div className="tw-font-medium">Players per match</div>
                <Form.Control
                  type="number"
                  min={1}
                  value={activityPlayersPerTeam ?? ""}
                  onChange={(e) => setActivityPlayersPerTeam(e.target.value)}
                  style={{ width: 100 }}
                />
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={handleSavePlayersPerTeam}
                  disabled={isSavingPlayersPerTeam}
                >
                  {isSavingPlayersPerTeam ? (
                    <>
                      Saving{" "}
                      <Spinner animation="border" role="status" size="sm" />
                    </>
                  ) : (
                    "Edit"
                  )}
                </Button>
              </div>

              <div className="tw-mb-2 tw-flex tw-font-semibold">
                <div className="tw-flex-1">Match</div>
                <div style={{ width: 80 }}>Order</div>
                <div style={{ width: 120 }}>Half time</div>
                <div style={{ width: 80 }} />
              </div>

              {orderMatches.length === 0 ? (
                <div>No matches found.</div>
              ) : (
                orderMatches.map((match, index) => (
                  <div
                    key={match.id}
                    className="tw-flex tw-items-center tw-gap-2 tw-mb-2"
                  >
                    <div className="tw-flex-1">Match {match.id}</div>
                    <div className="tw-text-center" style={{ width: 80 }}>
                      {typeof match.order_in_activity !== "undefined"
                        ? match.order_in_activity
                        : index}
                    </div>
                    <div style={{ width: 120 }}>
                      <Form.Control
                        type="number"
                        min={1}
                        value={
                          typeof matchTimes[match.id] !== "undefined"
                            ? matchTimes[match.id]
                            : ""
                        }
                        onChange={(e) =>
                          handleMatchTimeChange(match.id, e.target.value)
                        }
                      />
                    </div>
                    <div style={{ width: 90 }}>
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => handleSaveMatchTime(match.id)}
                        disabled={!!savingMatchTimes[match.id]}
                      >
                        {savingMatchTimes[match.id] ? (
                          <>
                            Saving{" "}
                            <Spinner
                              animation="border"
                              role="status"
                              size="sm"
                            />
                          </>
                        ) : (
                          "Edit"
                        )}
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowActivitySetupModal(false)}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Copy Activity Modal */}
      <Modal show={showCopyActivityModal} onHide={closeCopyActivityModal}>
        <Modal.Header closeButton>
          <Modal.Title>
            Copy activity
            {copyActivityOrder ? ` (order ${copyActivityOrder.id})` : ""}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="tw-flex tw-flex-col tw-gap-4">
            <Form.Group>
              <Form.Label>Choose environment</Form.Label>
              <Form.Select
                value={copyActivityEnvironment}
                onChange={async (e) => {
                  const env = e.target.value;
                  setCopyActivityEnvironment(env);
                  setSelectedExternalClientName("");
                  setExternalClients([]);
                  setExternalClientsPage(1);
                  setExternalClientsCount(0);
                  setExternalClientsHasMore(false);
                  await fetchExternalClients({
                    environment: env,
                    page: 1,
                    append: false,
                  });
                }}
              >
                <option value="devbox">devbox</option>
                <option value="stage">stage</option>
                <option value="prod">prod</option>
              </Form.Select>
            </Form.Group>

            <Form.Group>
              <Form.Label>Choose user on target environment</Form.Label>
              <InfiniteScrollSelect
                className="tw-w-full"
                options={externalClients}
                value={selectedExternalClientName}
                onChange={(e) => setSelectedExternalClientName(e.target.value)}
                disabled={!copyActivityEnvironment}
                hasMore={externalClientsHasMore}
                isLoading={externalClientsLoading}
                onLoadMore={loadMoreExternalClients}
                placeholder={
                  externalClientsLoading ? "Loading users..." : "Select a user"
                }
              />
              {externalClientsCount ? (
                <div className="tw-text-xs tw-text-gray-500 tw-mt-1">
                  Total: {externalClientsCount}
                </div>
              ) : null}
            </Form.Group>
          </div>
        </Modal.Body>
        <Modal.Footer>
          {(() => {
            const activityId = getActivityIdFromOrder(copyActivityOrder);
            // Select stores getOptionValue(option) = option.id (e.g. "5-0"), not name
            const selected = externalClients.find(
              (c) => c.id === selectedExternalClientName
            );
            const userId = selected?.userId ?? null;
            const copyDisabledReasons = {
              isCopyingActivity,
              noEnvironment: !copyActivityEnvironment,
              noActivityId: !activityId,
              noUserId: !userId,
            };
            const isCopyDisabled = Object.values(copyDisabledReasons).some(
              Boolean
            );
            return (
              <>
                <Button variant="secondary" onClick={closeCopyActivityModal}>
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  onClick={async () => {
                    try {
                      setIsCopyingActivity(true);
                      await ordersApi.copyActivity({
                        environment: copyActivityEnvironment,
                        user_id: userId,
                        activity_id: activityId,
                      });
                      toast.success("Activity copied successfully.");
                      closeCopyActivityModal();
                    } catch (err) {
                      console.log(err);
                      toast.error(
                        err.response?.data?.message ||
                          JSON.stringify(err.response?.data)?.slice(0, 200) ||
                          err.message ||
                          "Failed to copy activity."
                      );
                    } finally {
                      setIsCopyingActivity(false);
                    }
                  }}
                  disabled={isCopyDisabled}
                >
                  {isCopyingActivity ? (
                    <>
                      Copying{" "}
                      <Spinner animation="border" role="status" size="sm" />
                    </>
                  ) : (
                    "Copy"
                  )}
                </Button>
              </>
            );
          })()}
        </Modal.Footer>
      </Modal>

      {/* Edit Colors Modal (reused) */}
      <Modal show={showColorModal} onHide={() => setShowColorModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Edit Team Colors</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="tw-flex tw-gap-4">
            <div className="mb-3">
              <div className="tw-w-32 mb-2">Left team color:</div>
              <Form.Select
                className="tw-w-36"
                value={editTeams.homeTeam.color}
                onChange={(e) =>
                  setEditTeams({
                    ...editTeams,
                    homeTeam: { ...editTeams.homeTeam, color: e.target.value },
                  })
                }
              >
                <option value="">Select color</option>
                {(editingMatch?.match_colors || []).length === 0 ? (
                  <option disabled>No colors available</option>
                ) : (
                  TEAM_COLORS.filter((color) =>
                    editingMatch.match_colors.includes(color.value)
                  ).map((color) => (
                    <option key={color.value} value={color.value}>
                      {color.label}
                    </option>
                  ))
                )}
              </Form.Select>
            </div>
            <div>
              <div className="tw-w-32 mb-2">Right team color:</div>
              <Form.Select
                className="tw-w-36"
                value={editTeams.guestTeam.color}
                onChange={(e) =>
                  setEditTeams({
                    ...editTeams,
                    guestTeam: {
                      ...editTeams.guestTeam,
                      color: e.target.value,
                    },
                  })
                }
              >
                <option value="">Select color</option>
                {(editingMatch?.match_colors || []).length === 0 ? (
                  <option disabled>No colors available</option>
                ) : (
                  TEAM_COLORS.filter((color) =>
                    editingMatch.match_colors.includes(color.value)
                  ).map((color) => (
                    <option key={color.value} value={color.value}>
                      {color.label}
                    </option>
                  ))
                )}
              </Form.Select>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowColorModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleColorEdit}>
            Save Changes
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default Orders;
